export interface BlogPost {
  slug: string;
  title: string;
  description: string;
  coverEmoji: string;
  category: string;
  publishedAt: string;
  readingTime: number; // minutes
  author: string;
  tags: string[];
  content: BlogSection[];
}

export interface BlogSection {
  type: 'h2' | 'h3' | 'p' | 'ul' | 'ol' | 'table' | 'cta' | 'quote' | 'tip';
  text?: string;
  items?: string[];
  headers?: string[];
  rows?: string[][];
  href?: string;  // optional override for cta link target
  label?: string; // optional override for cta button text
}

export const blogPosts: BlogPost[] = [
  // ─────────────────────────────────────────────────────────────────
  // POST 1: Best Retrospective Tools 2026
  // ─────────────────────────────────────────────────────────────────
  {
    slug: 'best-retrospective-tools-2026',
    title: 'Best 5 Retrospective Tools in 2026 (Honest Comparison)',
    description:
      'We compared the top 5 online retrospective tools in 2026: features, pricing, ease of use, and real-time collaboration. Find out which one is right for your agile team.',
    coverEmoji: '🏆',
    category: 'Comparison',
    publishedAt: '2026-02-27',
    readingTime: 8,
    author: 'Retrosive Team',
    tags: [
      'retrospective tools',
      'agile retrospective',
      'best retro tool 2026',
      'sprint retrospective',
      'scrum tools',
    ],
    content: [
      {
        type: 'p',
        text: 'Running great sprint retrospectives requires the right tool. In 2026, the market is full of options, from free basic boards to enterprise-grade platforms. We tested each one ourselves so you don\'t have to.',
      },
      {
        type: 'p',
        text: 'Our criteria: ease of use, real-time collaboration quality, template variety, pricing fairness, and how well it supports both remote and in-person teams.',
      },
      {
        type: 'h2',
        text: '1. Retrosive: Best Overall Retrospective Tool',
      },
      {
        type: 'p',
        text: 'Retrosive tops our list in 2026 for a simple reason: it nails the balance between power and simplicity. You don\'t need an onboarding session to run your first retro, just create a board, share the link, and your team can start adding cards in seconds.',
      },
      {
        type: 'h3',
        text: 'Why Retrosive is #1',
      },
      {
        type: 'ul',
        items: [
          '⚡ Real-time canvas collaboration: see teammates\' cursors and cards appear live, no refresh needed',
          '🎭 Anonymous mode: team members write honestly without fear of judgment, leading to more actionable feedback',
          '🔒 Private writing phase: prevent groupthink by having everyone write independently before seeing others\' cards',
          '📋 12+ built-in templates: Start/Stop/Continue, 4Ls, Mad-Sad-Glad, Starfish, and more',
          '✅ Built-in action items: assign owners and due dates directly in the retro board',
          '⏱️ Facilitator timer: keep sessions focused with a built-in countdown visible to all participants',
          '📊 Export to PDF and CSV: share results with stakeholders easily',
          '💰 Transparent, affordable pricing: generous free tier, no surprise charges',
        ],
      },
      {
        type: 'p',
        text: 'What truly differentiates Retrosive is the canvas-based board. Unlike tools that feel like a spreadsheet or a simple sticky-note wall, Retrosive\'s spatial canvas lets teams group related ideas organically, just like a physical whiteboard, but better.',
      },
      {
        type: 'quote',
        text: '"We switched from our old retro tool to Retrosive and our team engagement in retrospectives went up noticeably within the first month. The anonymous mode made a huge difference."',
      },
      {
        type: 'tip',
        text: 'Start for free at retrosive.com, no credit card required. You can run unlimited retrospectives on the free plan.',
      },
      {
        type: 'h2',
        text: '2. EasyRetro: Good for Simple Boards',
      },
      {
        type: 'p',
        text: 'EasyRetro (formerly FunRetro) is one of the oldest names in the retro tool space. It\'s simple to use and gets the job done for basic retrospectives. The interface is clean and straightforward.',
      },
      {
        type: 'h3',
        text: 'Pros',
      },
      {
        type: 'ul',
        items: [
          'Very easy to start: no account required for basic use',
          'Clean and minimal UI',
          'Voting system works well',
          'Good for teams that just need a simple digital sticky board',
        ],
      },
      {
        type: 'h3',
        text: 'Cons',
      },
      {
        type: 'ul',
        items: [
          'No real-time canvas: collaboration feels less fluid',
          'Limited template library',
          'No built-in action item tracking',
          'Private writing mode not available on free plan',
          'Paid plans are relatively expensive compared to alternatives',
        ],
      },
      {
        type: 'p',
        text: 'EasyRetro is fine for small teams doing occasional retrospectives, but teams that hold weekly retros will quickly outgrow its limitations.',
      },
      {
        type: 'h2',
        text: '3. Parabol: Best Open Source Option',
      },
      {
        type: 'p',
        text: 'Parabol is an open-source retrospective and meeting tool with a strong focus on async-first workflows. It\'s particularly popular in engineering-heavy organizations that appreciate the ability to self-host.',
      },
      {
        type: 'h3',
        text: 'Pros',
      },
      {
        type: 'ul',
        items: [
          'Open source: full transparency and self-hosting option',
          'Structured meeting flows enforce good retro habits',
          'Integrates with Jira and GitHub for action item sync',
          'Good for async retrospectives',
        ],
      },
      {
        type: 'h3',
        text: 'Cons',
      },
      {
        type: 'ul',
        items: [
          'Steep learning curve: first session often requires guidance',
          'UI feels dated compared to modern canvas tools',
          'Self-hosting requires DevOps effort',
          'Less flexibility for custom retro formats',
          'The rigid meeting structure can feel constraining for creative retros',
        ],
      },
      {
        type: 'p',
        text: 'Parabol is a solid choice if your team is technical and values open-source tooling. For most teams wanting a fast, smooth experience, however, it\'s not the easiest to get started.',
      },
      {
        type: 'h2',
        text: '4. Metro Retro (Ludi): Best Visual Design',
      },
      {
        type: 'p',
        text: 'Metro Retro, now rebranded as Ludi, has arguably the most visually delightful interface in the retro tool space. It supports a wide range of fun activities and ice-breakers beyond traditional retrospectives.',
      },
      {
        type: 'h3',
        text: 'Pros',
      },
      {
        type: 'ul',
        items: [
          'Beautiful, highly polished UI with fun visual elements',
          'Great for energizing team culture and engagement',
          'Wide variety of templates including non-retro activities',
          'Good real-time collaboration',
        ],
      },
      {
        type: 'h3',
        text: 'Cons',
      },
      {
        type: 'ul',
        items: [
          'Pricing is significantly higher than alternatives',
          'Free plan is very limited: quickly hits walls',
          'Can feel "too fun" for serious process-driven retrospectives',
          'Action item tracking is basic',
          'Performance can lag with large teams',
        ],
      },
      {
        type: 'p',
        text: 'If budget isn\'t a concern and your team loves highly visual, engaging sessions, Metro Retro/Ludi is worth trying. For cost-conscious teams, the price-to-value ratio doesn\'t compare favorably to Retrosive.',
      },
      {
        type: 'h2',
        text: '5. TeamRetro: Best for Enterprise',
      },
      {
        type: 'p',
        text: 'TeamRetro targets larger enterprise teams with features like SSO, advanced analytics across multiple teams, and health check surveys. It\'s a mature product with a professional feature set.',
      },
      {
        type: 'h3',
        text: 'Pros',
      },
      {
        type: 'ul',
        items: [
          'Strong analytics and team health tracking',
          'SSO and enterprise security features',
          'Good multi-team management',
          'Structured retrospective process with good facilitation guides',
        ],
      },
      {
        type: 'h3',
        text: 'Cons',
      },
      {
        type: 'ul',
        items: [
          'Expensive: pricing jumps sharply for team plans',
          'No meaningful free tier',
          'UI feels more "corporate" and less engaging',
          'Overkill for small-to-medium teams',
          'Setup and configuration takes time',
        ],
      },
      {
        type: 'p',
        text: 'TeamRetro makes sense for large enterprises managing 20+ teams who need centralized reporting. For startups and growing teams, the cost and complexity aren\'t justified.',
      },
      {
        type: 'h2',
        text: 'Side-by-Side Comparison',
      },
      {
        type: 'table',
        headers: ['Tool', 'Free Plan', 'Real-time Canvas', 'Anonymous Mode', 'Action Items', 'Best For'],
        rows: [
          ['Retrosive', '✅ Generous', '✅ Yes', '✅ Yes', '✅ Yes', 'All team sizes'],
          ['EasyRetro', '✅ Limited', '⚠️ Basic', '⚠️ Paid only', '❌ No', 'Small teams'],
          ['Parabol', '✅ Yes', '⚠️ Limited', '✅ Yes', '✅ Yes', 'Technical teams'],
          ['Metro Retro/Ludi', '⚠️ Very limited', '✅ Yes', '✅ Yes', '⚠️ Basic', 'Culture-focused'],
          ['TeamRetro', '❌ No', '⚠️ Basic', '✅ Yes', '✅ Yes', 'Enterprise'],
        ],
      },
      {
        type: 'h2',
        text: 'Our Verdict',
      },
      {
        type: 'p',
        text: 'For the vast majority of agile teams in 2026, Retrosive offers the best combination of features, ease of use, and pricing. The real-time canvas feels like a natural extension of a physical whiteboard, the anonymous mode unlocks genuine team feedback, and the free plan is genuinely useful, not a bait-and-switch.',
      },
      {
        type: 'p',
        text: 'If you\'re a large enterprise with specific compliance requirements, TeamRetro is worth evaluating. If you\'re open source-minded and technical, Parabol is a valid alternative. For everyone else, start with Retrosive.',
      },
      {
        type: 'cta',
        text: 'Try Retrosive free, no credit card required',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────
  // POST 2: How to Run Effective Sprint Retrospectives
  // ─────────────────────────────────────────────────────────────────
  {
    slug: 'how-to-run-effective-sprint-retrospective',
    title: 'How to Run an Effective Sprint Retrospective in 2026',
    description:
      'A practical, step-by-step guide to running sprint retrospectives that actually lead to improvement, not just another meeting. Includes tips for remote teams.',
    coverEmoji: '🔄',
    category: 'Guide',
    publishedAt: '2026-02-20',
    readingTime: 7,
    author: 'Retrosive Team',
    tags: [
      'sprint retrospective',
      'agile retrospective guide',
      'how to run retrospective',
      'scrum retrospective tips',
      'remote retrospective',
    ],
    content: [
      {
        type: 'p',
        text: 'A sprint retrospective is one of the most valuable ceremonies in Scrum, yet it\'s often the one that gets cut when time is tight. When done right, it\'s not just another meeting: it\'s the mechanism that drives continuous team improvement.',
      },
      {
        type: 'p',
        text: 'This guide covers everything from preparation to follow-through, so your retros produce real, lasting changes.',
      },
      {
        type: 'h2',
        text: 'What Is a Sprint Retrospective?',
      },
      {
        type: 'p',
        text: 'A sprint retrospective is a time-boxed meeting held at the end of each sprint where the team reflects on how they worked together, not what they built (that\'s the sprint review). The goal is to identify specific improvements for the next sprint.',
      },
      {
        type: 'tip',
        text: 'The Scrum Guide recommends a maximum of 3 hours for a one-month sprint. For 2-week sprints, 90 minutes is the sweet spot.',
      },
      {
        type: 'h2',
        text: 'Step 1: Set the Stage (5 minutes)',
      },
      {
        type: 'p',
        text: 'Before diving into reflection, create psychological safety. Team members need to feel comfortable sharing honestly, especially about problems.',
      },
      {
        type: 'ul',
        items: [
          'Open with a brief check-in question (e.g., "In one word, how was this sprint for you?")',
          'Remind the team of the Prime Directive: everyone did the best they could with what they knew',
          'Set expectations: this is a blame-free zone focused on systems, not people',
        ],
      },
      {
        type: 'h2',
        text: 'Step 2: Gather Data Independently (10-15 minutes)',
      },
      {
        type: 'p',
        text: 'Before anyone shares, have each team member write their observations independently. This prevents anchoring bias (where the first person to speak shapes everyone else\'s view) and ensures quieter team members contribute equally.',
      },
      {
        type: 'p',
        text: 'Use the private writing mode in your retro tool, where cards are hidden until everyone is done writing. This is one of the most impactful features for getting honest, diverse input.',
      },
      {
        type: 'h2',
        text: 'Step 3: Generate Insights Through Discussion (20-30 minutes)',
      },
      {
        type: 'p',
        text: 'Reveal all cards at once, group similar themes together, and let the team discuss. Focus on understanding root causes, not assigning blame.',
      },
      {
        type: 'ul',
        items: [
          'Ask "why did this happen?" at least 3-5 times to find root causes (the 5 Whys technique)',
          'Look for patterns that repeat across multiple sprints',
          'Celebrate what went well: positive reinforcement matters',
          'Use dot voting to surface the most important topics when time is limited',
        ],
      },
      {
        type: 'h2',
        text: 'Step 4: Decide What to Do (10-15 minutes)',
      },
      {
        type: 'p',
        text: 'Insights without actions are useless. Commit to 1-3 specific, measurable improvements, not vague resolutions. Each action item should have an owner and a due date.',
      },
      {
        type: 'quote',
        text: '"The worst retrospective outcome is a list of action items that nobody looks at again. Keep it to three actions maximum, and follow up in the next retro."',
      },
      {
        type: 'h2',
        text: 'Step 5: Close the Retrospective (5 minutes)',
      },
      {
        type: 'p',
        text: 'End with a quick retrospective-on-the-retrospective: ask "How useful was this session on a scale of 1-5?" This helps you improve your retrospective process over time.',
      },
      {
        type: 'h2',
        text: 'Tips for Remote Retrospectives',
      },
      {
        type: 'ul',
        items: [
          'Use a purpose-built retro tool (not just a video call with a shared doc)',
          'Enable cameras: seeing faces increases engagement and psychological safety',
          'Set a strict time-box for each phase and use a visible timer',
          'Use anonymous mode to get honest feedback regardless of hierarchy',
          'Send the board link 10 minutes early so nobody wastes time on setup',
          'Record key decisions and action items in the tool itself, not a separate document',
        ],
      },
      {
        type: 'h2',
        text: 'Common Retrospective Mistakes to Avoid',
      },
      {
        type: 'ul',
        items: [
          'Same format every sprint: rotate templates to keep it fresh',
          'No follow-up on previous action items: always start with "what did we do about last sprint\'s actions?"',
          'Letting one person dominate: use structured formats and private writing',
          'Running over time: a skilled facilitator keeps discussions on track',
          'Skipping retros when the team is busy: that\'s exactly when you need them most',
        ],
      },
      {
        type: 'cta',
        text: 'Run your next retrospective with Retrosive: free, no credit card required',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────
  // POST 3: Retrospective Templates Guide
  // ─────────────────────────────────────────────────────────────────
  {
    slug: 'retrospective-templates-guide',
    title: 'The Complete Guide to Retrospective Templates (2026)',
    description:
      'Explore the most effective retrospective templates for agile teams: Start/Stop/Continue, 4Ls, Mad-Sad-Glad, Starfish, and more. Learn when to use each one.',
    coverEmoji: '📋',
    category: 'Templates',
    publishedAt: '2026-02-15',
    readingTime: 6,
    author: 'Retrosive Team',
    tags: [
      'retrospective templates',
      'start stop continue',
      '4ls retrospective',
      'mad sad glad',
      'starfish retrospective',
      'agile retrospective formats',
    ],
    content: [
      {
        type: 'p',
        text: 'Using the same retrospective format every sprint leads to stale feedback. Rotating templates keeps your team engaged and surfaces different kinds of insights. Here\'s a guide to the most effective retrospective templates and when to use them.',
      },
      {
        type: 'h2',
        text: '1. Start, Stop, Continue',
      },
      {
        type: 'p',
        text: 'The classic. Simple and effective for any team at any stage. Three columns: what should we start doing, stop doing, and continue doing?',
      },
      {
        type: 'ul',
        items: [
          'Best for: Teams new to retrospectives, any sprint',
          'Time needed: 60-90 minutes',
          'Strength: Actionable by design, "start" cards are natural action items',
        ],
      },
      {
        type: 'h2',
        text: '2. 4Ls: Liked, Learned, Lacked, Longed For',
      },
      {
        type: 'p',
        text: 'The 4Ls format encourages deeper reflection than Start/Stop/Continue by separating emotional experience (Liked, Longed For) from learning (Learned) and gaps (Lacked).',
      },
      {
        type: 'ul',
        items: [
          'Best for: After a challenging sprint or major milestone',
          'Time needed: 75-90 minutes',
          'Strength: Captures both process issues and team morale in one format',
        ],
      },
      {
        type: 'h2',
        text: '3. Mad, Sad, Glad',
      },
      {
        type: 'p',
        text: 'Focuses on emotional experience. Mad = frustrations, Sad = disappointments, Glad = celebrations. Great for understanding team morale.',
      },
      {
        type: 'ul',
        items: [
          'Best for: Teams experiencing friction or low morale',
          'Time needed: 60-75 minutes',
          'Strength: Opens emotional discussions that more structured formats might miss',
        ],
      },
      {
        type: 'h2',
        text: '4. Starfish',
      },
      {
        type: 'p',
        text: 'Five categories: Keep Doing, Stop Doing, Start Doing, Do More Of, Do Less Of. More nuanced than Start/Stop/Continue because it captures gradients.',
      },
      {
        type: 'ul',
        items: [
          'Best for: Mature teams wanting nuanced feedback',
          'Time needed: 90 minutes',
          'Strength: "More/Less" categories capture things that are good but need calibration',
        ],
      },
      {
        type: 'h2',
        text: '5. Sailboat',
      },
      {
        type: 'p',
        text: 'A visual metaphor: the sailboat represents the team, wind is what propels you forward, anchors are what slow you down, rocks ahead are risks, and the island is the goal.',
      },
      {
        type: 'ul',
        items: [
          'Best for: Strategic retrospectives, quarterly reviews',
          'Time needed: 90-120 minutes',
          'Strength: Connects day-to-day friction to bigger team goals',
        ],
      },
      {
        type: 'h2',
        text: '6. KALM: Keep, Add, Less, More',
      },
      {
        type: 'p',
        text: 'Similar to Starfish but simpler. KALM is particularly popular in product teams because it maps well to product prioritization thinking.',
      },
      {
        type: 'h2',
        text: 'Which Template Should You Use?',
      },
      {
        type: 'table',
        headers: ['Situation', 'Recommended Template'],
        rows: [
          ['First retrospective / new team', 'Start, Stop, Continue'],
          ['Low team morale', 'Mad, Sad, Glad'],
          ['After a difficult sprint', '4Ls'],
          ['Mature team wanting depth', 'Starfish'],
          ['Quarterly / milestone review', 'Sailboat'],
          ['Product-focused team', 'KALM'],
        ],
      },
      {
        type: 'tip',
        text: 'Rotate templates every 2-3 sprints. Even teams that love Start/Stop/Continue benefit from occasionally trying a different format, it surfaces blind spots.',
      },
      {
        type: 'p',
        text: 'All of these templates are available in Retrosive with one click. No setup needed, just pick a template and share the link with your team.',
      },
      {
        type: 'cta',
        text: 'Try all templates free on Retrosive',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────
  // POST 4: Sprint Retrospective Report Example
  // ─────────────────────────────────────────────────────────────────
  {
    slug: 'sprint-retrospective-report-example',
    title: 'Sprint Retrospective Report Example (Real + AI-Generated)',
    description:
      'See a complete sprint retrospective report example: manually written and AI-generated. Learn what to include in your retro report and how to generate one automatically.',
    coverEmoji: '📄',
    category: 'Example',
    publishedAt: '2026-03-01',
    readingTime: 6,
    author: 'Retrosive Team',
    tags: [
      'sprint retrospective report',
      'retro report example',
      'sprint retrospective summary',
      'agile retrospective',
      'scrum report',
    ],
    content: [
      {
        type: 'p',
        text: 'A sprint retrospective report is the written record of your retro session: what was discussed, what was decided, and what the team committed to improve. Unlike sprint review notes, the retro report focuses entirely on team process and working agreements.',
      },
      {
        type: 'h2',
        text: 'What to Include in a Sprint Retrospective Report',
      },
      {
        type: 'ul',
        items: [
          'Sprint number and date range',
          'Team name and participant count',
          'Format/template used (e.g. Start/Stop/Continue, 4Ls)',
          'Top-voted items per category (not every single card)',
          'Key themes and root causes discussed',
          'Action items with assigned owner and due date',
          'Status of previous sprint\'s action items',
        ],
      },
      {
        type: 'h2',
        text: 'Real Example: Sprint 24 Retrospective Report',
      },
      {
        type: 'p',
        text: 'Team: Platform Squad (5 engineers, 1 designer, 1 PM) · Sprint 24 (Feb 10–21, 2026) · Template: Start, Stop, Continue · Facilitator: Sarah (Scrum Master)',
      },
      {
        type: 'table',
        headers: ['Category', 'Top Items (votes)', 'Theme'],
        rows: [
          ['Start', 'Write PR descriptions before requesting review (6)', 'Code review quality'],
          ['Start', 'Async standup updates on Wednesdays (4)', 'Meeting overhead'],
          ['Stop', 'Merging to main without at least one review (8)', 'Code quality risk'],
          ['Stop', 'Over-committing in sprint planning (7)', 'Capacity planning'],
          ['Continue', 'Friday 30-min knowledge sharing sessions (9)', 'Team learning'],
          ['Continue', 'Daily frontend/backend sync calls (5)', 'Cross-team alignment'],
        ],
      },
      {
        type: 'h3',
        text: 'Action Items from Sprint 24',
      },
      {
        type: 'table',
        headers: ['Action Item', 'Owner', 'Due Date', 'Status'],
        rows: [
          ['Add PR description template to GitHub', 'Alex', 'Feb 28', 'Open'],
          ['Set up async standup bot in Slack', 'Maria', 'Feb 25', 'Open'],
          ['Enable "2 reviewers required" branch protection', 'Alex', 'Feb 28', 'Open'],
        ],
      },
      {
        type: 'h3',
        text: 'Previous Sprint Action Items (Sprint 23)',
      },
      {
        type: 'table',
        headers: ['Action Item', 'Owner', 'Status'],
        rows: [
          ['Add deployment checklist to runbook', 'Jin', 'Done'],
          ['Schedule bi-weekly design review', 'Sarah', 'Done'],
          ['Fix flaky integration tests', 'Dev team', 'In progress (moved to Sprint 24)'],
        ],
      },
      {
        type: 'h2',
        text: 'AI-Generated Report Example',
      },
      {
        type: 'p',
        text: 'The same retro board above, processed through an AI summary engine, produced this report in under 10 seconds:',
      },
      {
        type: 'quote',
        text: 'AI Summary: Sprint 24, Platform Squad (7 participants): The team\'s highest-priority concern was merging without review (8 votes), indicating a need for stronger branch protection policies. Closely related: over-commitment in sprint planning (7 votes) suggests the team consistently underestimates effort. These two themes are likely connected, pressure to ship quickly may be driving risky merge habits.\n\nOn the positive side, Friday knowledge sharing sessions received the most votes across all categories (9), suggesting this practice has strong buy-in and should be actively protected even under sprint pressure.\n\n3 action items committed, all assigned with due dates. Recommended: open Sprint 25 by reviewing all three with owners before starting planning.',
      },
      {
        type: 'h2',
        text: 'Why Teams Use AI-Generated Retro Reports',
      },
      {
        type: 'ul',
        items: [
          'Saves 20–30 minutes of post-session write-up time per sprint',
          'Identifies cross-sprint patterns human facilitators may miss',
          'Produces a consistent, shareable format for stakeholders',
          'Automatically surfaces the most important themes by vote count',
          'Generates a follow-up agenda item for the next sprint\'s kickoff',
        ],
      },
      {
        type: 'tip',
        text: 'The best time to generate your AI report is immediately after the retro, while the discussion context is fresh. Send it to the team Slack channel within the same hour.',
      },
      {
        type: 'cta',
        text: 'Generate your sprint retrospective report automatically with AI',
        href: '/ai-retrospective-summary',
        label: 'Generate AI Report →',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────
  // POST 5: Retrospective Summary Example
  // ─────────────────────────────────────────────────────────────────
  {
    slug: 'retrospective-summary-example',
    title: 'Retrospective Summary Example (Templates You Can Copy)',
    description:
      'See real retrospective summary examples: short-form, long-form, and AI-generated. Includes free templates you can copy for your next sprint retro.',
    coverEmoji: '📝',
    category: 'Example',
    publishedAt: '2026-03-01',
    readingTime: 5,
    author: 'Retrosive Team',
    tags: [
      'retrospective summary',
      'retro summary example',
      'sprint retrospective summary',
      'retro report template',
      'agile retrospective',
    ],
    content: [
      {
        type: 'p',
        text: 'A retrospective summary is a concise written record of your retro session, different from a full report in that it prioritizes speed and readability over completeness. A good summary can be read in 90 seconds and tells the reader exactly what the team decided.',
      },
      {
        type: 'h2',
        text: 'Short-Form vs. Long-Form: Which Do You Need?',
      },
      {
        type: 'table',
        headers: ['', 'Short-Form Summary', 'Long-Form Report'],
        rows: [
          ['Length', '150–300 words', '500–800 words'],
          ['Audience', 'The team itself', 'Team + managers + stakeholders'],
          ['Includes', 'Top 3 themes + action items', 'All voted items + root causes + history'],
          ['Best for', 'Slack post, team wiki', 'Quarterly review, leadership sync'],
          ['Time to write', '5–10 minutes', '20–30 minutes'],
        ],
      },
      {
        type: 'h2',
        text: 'Real Example: Short-Form Summary',
      },
      {
        type: 'p',
        text: 'Team: Backend Squad · Sprint 17 · Template: Mad, Sad, Glad',
      },
      {
        type: 'quote',
        text: 'Sprint 17 Retro Summary (Backend Squad, 6 participants)\n\nTop themes: The team is frustrated by unclear ticket acceptance criteria causing rework. Two engineers flagged deployment pipeline slowness as a blocker to flow. On the bright side, the new pair-programming Thursdays are getting positive traction.\n\nAction items:\n1. PM to add acceptance criteria template to Jira → owner: Priya → due: Mar 7\n2. Devops to investigate pipeline bottleneck → owner: Tom → due: Mar 10\n3. Continue pair-programming sessions, add to team charter → owner: All\n\nNext retro: we\'ll open by reviewing these 3 items.',
      },
      {
        type: 'h2',
        text: 'Real Example: Long-Form Summary',
      },
      {
        type: 'h3',
        text: 'Sprint 17: Backend Squad Retrospective Report',
      },
      {
        type: 'ul',
        items: [
          'Date: March 3, 2026 · Duration: 75 minutes · Format: Mad, Sad, Glad',
          'Participants: 6 of 6 (full attendance)',
          'Facilitator: Tom (rotating)',
        ],
      },
      {
        type: 'h3',
        text: 'What Made the Team Mad',
      },
      {
        type: 'ul',
        items: [
          'Tickets missing acceptance criteria, leading to rework after review (5 votes)',
          'Deployment pipeline taking 18+ minutes: developers losing context between push and feedback (4 votes)',
          'Incomplete handoff notes from the design team before dev starts (3 votes)',
        ],
      },
      {
        type: 'h3',
        text: 'What Made the Team Sad',
      },
      {
        type: 'ul',
        items: [
          'Sprint 16 carry-over items still not resolved (3 votes)',
          'No time for refactoring the authentication module despite repeated discussion (2 votes)',
        ],
      },
      {
        type: 'h3',
        text: 'What Made the Team Glad',
      },
      {
        type: 'ul',
        items: [
          'Pair-programming Thursdays: team is learning more from each other (6 votes)',
          'Successful zero-downtime deployment of the payments service (5 votes)',
          'Improved PR review turnaround: average down from 48h to 18h (4 votes)',
        ],
      },
      {
        type: 'h2',
        text: 'AI-Generated Summary Example',
      },
      {
        type: 'quote',
        text: 'AI Summary: Sprint 17, Backend Squad: The dominant theme this retro is process friction upstream from development. Unclear acceptance criteria and slow deployment feedback loops are the top two pain points, both reduce flow and increase rework. These are systemic issues worth prioritizing.\n\nThe team\'s morale indicators are mixed: frustration with carry-over and tech debt backlogs, but genuine enthusiasm for pair programming and improved review speed. The pair-programming sessions appear to be a high-leverage practice worth formalizing.\n\nRecommendation: address acceptance criteria quality immediately (sprint 18), schedule a focused spike on pipeline optimization, and formally add Thursday pairing to the team working agreement.',
      },
      {
        type: 'cta',
        text: 'Skip the write-up, generate your retrospective summary automatically',
        href: '/ai-retrospective-summary',
        label: 'Generate AI Summary →',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────
  // POST 6: How to Summarize Sprint Retrospective
  // ─────────────────────────────────────────────────────────────────
  {
    slug: 'how-to-summarize-sprint-retrospective',
    title: 'How to Summarize a Sprint Retrospective (Step-by-Step + AI Shortcut)',
    description:
      'Learn how to write a clear sprint retrospective summary your whole team will read. Includes a 5-step process, real examples, and an AI shortcut that takes 10 seconds.',
    coverEmoji: '✍️',
    category: 'Guide',
    publishedAt: '2026-03-01',
    readingTime: 5,
    author: 'Retrosive Team',
    tags: [
      'how to summarize sprint retrospective',
      'retro summary',
      'sprint retrospective guide',
      'agile retrospective',
      'scrum',
    ],
    content: [
      {
        type: 'p',
        text: 'Most teams run a good retrospective, then let the insights die in a sticky-note board nobody looks at again. Writing a clear summary, and distributing it within the same day, is the difference between retros that drive change and retros that feel like theater.',
      },
      {
        type: 'h2',
        text: 'Step 1: Capture Key Themes During the Session (Not After)',
      },
      {
        type: 'p',
        text: 'The easiest time to summarize is while the discussion is still happening. As your team votes and discusses, keep a parallel note of the 3–4 themes that keep surfacing. Don\'t try to capture every card. Capture the patterns.',
      },
      {
        type: 'tip',
        text: 'Use your retro tool\'s export feature to get all cards as a structured list. Retrosive exports to PDF and CSV with one click, giving you the raw material for your summary instantly.',
      },
      {
        type: 'h2',
        text: 'Step 2: Group by Theme, Not by Column',
      },
      {
        type: 'p',
        text: 'Columns (Start, Stop, Continue) are for organizing discussion, not for your summary. Your readers care about themes: "code review process needs work" cuts across multiple columns and is more actionable than listing every card under each heading.',
      },
      {
        type: 'ul',
        items: [
          'Bad: List every card in every column',
          'Good: "Three items about code review quality emerged, all pointing to the same root cause: unclear definition of done"',
          'Good: "Team morale theme: deployment anxiety is draining focus"',
        ],
      },
      {
        type: 'h2',
        text: 'Step 3: Write the Action Items First',
      },
      {
        type: 'p',
        text: 'Your readers (especially those who weren\'t in the room) care most about what\'s going to change. Lead with action items, then provide context. Each action item needs: what, who, when.',
      },
      {
        type: 'quote',
        text: 'Action items always lead. Context follows. Never bury the commitments at the bottom of a wall of text.',
      },
      {
        type: 'h2',
        text: 'Step 4: Write for Two Audiences',
      },
      {
        type: 'p',
        text: 'Your summary will be read by your team (who were there) and your manager/stakeholders (who weren\'t). Write with the non-participant in mind: enough context to understand the decision, not so much that it takes 10 minutes to read.',
      },
      {
        type: 'table',
        headers: ['Section', 'What to include', 'Word target'],
        rows: [
          ['Opening line', 'Team, sprint, template, participant count', '1 sentence'],
          ['Key themes', 'Top 2–3 patterns from voting and discussion', '3–5 bullet points'],
          ['Action items', 'Each with owner and due date', '3–5 items max'],
          ['Previous sprint check', 'Status of last sprint\'s action items', '2–3 lines'],
        ],
      },
      {
        type: 'h2',
        text: 'Step 5: Distribute Within 24 Hours',
      },
      {
        type: 'p',
        text: 'A summary sent a week after the retro is nearly useless. People have moved on. Send it to your team channel the same day, ideally within an hour of the session ending. Include the action items in a reminder bot or task tracker so they don\'t get buried.',
      },
      {
        type: 'h2',
        text: 'What a Good Summary Looks Like',
      },
      {
        type: 'quote',
        text: 'Sprint 19 Retro: Mobile Squad (5 participants, Start/Stop/Continue)\n\nMain themes: Test coverage gaps are creating release anxiety. Team needs clearer ownership for QA tasks during the sprint, currently falling between engineers.\n\nAction items:\n1. Add QA owner field to sprint ticket template → Luis → Mar 14\n2. Set minimum coverage threshold of 80% in CI pipeline → Dev team → Mar 17\n3. Schedule 1h "testing hour" on Wednesdays → All → Start next sprint\n\nPrevious actions: deployment runbook update (done), design review sync (done), flaky test fix (carried to Sprint 20).',
      },
      {
        type: 'h2',
        text: 'The AI Shortcut: Generate Your Summary in 10 Seconds',
      },
      {
        type: 'p',
        text: 'If your team uses Retrosive, you can skip steps 1–4 entirely. The AI report feature reads your board (all the cards, all the votes, all the groupings) and writes a structured summary with themes, action items, and recommendations. It takes about 10 seconds.',
      },
      {
        type: 'ul',
        items: [
          'No copy-pasting sticky notes into a doc',
          'No trying to remember what was discussed while you were facilitating',
          'No formatting a table in Confluence at 6pm on a Friday',
          'Just: run retro, click "Generate Report", share with team',
        ],
      },
      {
        type: 'cta',
        text: 'Generate your sprint retrospective summary automatically',
        href: '/ai-retrospective-summary',
        label: 'Try AI Summary →',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────
  // POST 7: Retrospective Action Item Examples
  // ─────────────────────────────────────────────────────────────────
  {
    slug: 'retrospective-action-item-examples',
    title: 'Retrospective Action Item Examples (Good vs. Bad)',
    description:
      'See 15+ retrospective action item examples and learn what separates effective commitments from vague intentions. Includes a template you can copy for your next retro.',
    coverEmoji: '✅',
    category: 'Example',
    publishedAt: '2026-03-01',
    readingTime: 6,
    author: 'Retrosive Team',
    tags: [
      'retrospective action items',
      'action item examples',
      'sprint retrospective actions',
      'agile improvement',
      'scrum retrospective',
    ],
    content: [
      {
        type: 'p',
        text: 'The most common reason retrospectives fail to drive change isn\'t bad discussion, it\'s bad action items. Vague commitments like "improve communication" or "fix the deployment process" get forgotten within a sprint. Good action items are specific, owned, and time-bound.',
      },
      {
        type: 'h2',
        text: 'What Makes a Good Retrospective Action Item?',
      },
      {
        type: 'ul',
        items: [
          'Specific: describes exactly what will change, not just a direction',
          'Owned: one named person is responsible (not "the team")',
          'Time-bound: has a concrete due date, not "next sprint"',
          'Small: can realistically be completed in 1–2 weeks',
          'Verifiable: you\'ll know for certain when it\'s done',
        ],
      },
      {
        type: 'h2',
        text: 'Bad vs. Good: Side-by-Side Examples',
      },
      {
        type: 'table',
        headers: ['Bad Action Item', 'Why It Fails', 'Good Version'],
        rows: [
          ['Improve communication', 'Too vague: what will actually change?', 'Post a standup update in Slack by 10am daily (all team members, starts Monday)'],
          ['Fix the deployment pipeline', 'No owner, no deadline', 'Tom investigates and resolves P95 build time exceeding 15 minutes (due Mar 14)'],
          ['The team should write better tickets', '"The team" owns nothing', 'PM adds acceptance criteria to all tickets before sprint planning (Priya, next sprint)'],
          ['Do more code reviews', 'Not measurable', 'Each PR requires 2 approvals before merge, enabled via GitHub branch protection (Alex, Mar 10)'],
          ['Improve test coverage', 'No baseline, no target', 'Set 70% minimum coverage threshold in CI, fails the build below that (Dev team, Mar 17)'],
          ['Talk less in standups', 'No format to enforce it', 'Standup capped at 15 minutes using a timer, facilitator rotates daily (starts Monday)'],
        ],
      },
      {
        type: 'h2',
        text: '15+ Action Item Examples by Category',
      },
      {
        type: 'h3',
        text: 'Code Quality',
      },
      {
        type: 'ul',
        items: [
          'Enable linting in CI pipeline: zero warnings allowed (owner: Alex, due: Mar 10)',
          'Add a PR description template to GitHub that requires "What changed" and "How to test" sections (owner: Maria, due: Mar 7)',
          'Schedule monthly "code clean-up Friday": 2h of tech debt reduction per engineer (starts: Sprint 20)',
          'Add SonarQube scan step to CI and review results in next retro (owner: Dev team, due: end of sprint)',
        ],
      },
      {
        type: 'h3',
        text: 'Communication',
      },
      {
        type: 'ul',
        items: [
          'Post a written standup update in #team-dev by 9:30am on work-from-home days (all team members, starts Monday)',
          'Create a #decisions Slack channel to log architecture and product decisions (owner: Sarah, due: Mar 8)',
          'Add a 10-minute "blockers check" at the end of every daily standup (starts next sprint)',
          'Send a retro summary to the #platform channel within 2 hours of every retrospective (facilitator, ongoing)',
        ],
      },
      {
        type: 'h3',
        text: 'Process and Planning',
      },
      {
        type: 'ul',
        items: [
          'Cap sprint commitment to 80% of average velocity for next 2 sprints (owner: Scrum Master, starts Sprint 20)',
          'Add "Definition of Ready" checklist to sprint planning: each ticket must have mockups, acceptance criteria, and story points (owner: PM, due: Mar 12)',
          'Hold a 30-minute backlog refinement session every Wednesday at 2pm (standing meeting, owner: Sarah, starts: next week)',
          'Review and update team working agreements document (owner: all, due: Mar 15, schedule 1h workshop)',
        ],
      },
      {
        type: 'h3',
        text: 'Technical Infrastructure',
      },
      {
        type: 'ul',
        items: [
          'Set up Slack notification for deployment failures (owner: Tom, due: Mar 10)',
          'Document the on-call runbook with the top 5 incident scenarios (owner: Jin + Alex, due: Mar 20)',
          'Reduce average CI build time from 18 minutes to under 10 (owner: Tom, due: end of Sprint 21)',
        ],
      },
      {
        type: 'h2',
        text: 'How to Track Action Items Across Sprints',
      },
      {
        type: 'p',
        text: 'Create a dedicated action item board or section in your project management tool. At the start of every retrospective, review the previous sprint\'s action items before starting the current session. Ask: what was done, what wasn\'t, what needs to carry over?',
      },
      {
        type: 'tip',
        text: 'Retrosive includes built-in action item tracking. Each action item can have an owner, due date, and status, and carries forward automatically to your next board so you never lose track.',
      },
      {
        type: 'cta',
        text: 'Track action items and generate AI summaries: try Retrosive free',
        href: '/register',
        label: 'Get Started Free →',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────
  // POST 8: Start Stop Continue Retrospective Example
  // ─────────────────────────────────────────────────────────────────
  {
    slug: 'start-stop-continue-retrospective-example',
    title: 'Start Stop Continue Retrospective Example (With AI Summary)',
    description:
      'See a complete Start Stop Continue retrospective example: real team cards, voting results, action items, and an AI-generated summary report you can share with stakeholders.',
    coverEmoji: '🚦',
    category: 'Example',
    publishedAt: '2026-03-01',
    readingTime: 6,
    author: 'Retrosive Team',
    tags: [
      'start stop continue retrospective',
      'start stop continue example',
      'retrospective example',
      'agile retrospective',
      'scrum retrospective',
    ],
    content: [
      {
        type: 'p',
        text: 'Start, Stop, Continue is the most popular retrospective format for a reason: it\'s simple, immediately actionable, and works for any team. The three columns map directly to change: Start = new behaviors to adopt, Stop = harmful patterns to drop, Continue = good practices to protect.',
      },
      {
        type: 'h2',
        text: 'When to Use Start Stop Continue',
      },
      {
        type: 'ul',
        items: [
          'For any team, at any sprint stage: it\'s the most beginner-friendly format',
          'When you want the output to be immediately actionable',
          'When your team tends to ramble in retros: the three columns create focus',
          'When you want a format that\'s easy to run async or with a new facilitator',
        ],
      },
      {
        type: 'h2',
        text: 'Real Example: Product Team · Sprint 11',
      },
      {
        type: 'p',
        text: 'Team: Checkout Squad (4 engineers, 1 designer, 1 PM) · Sprint 11 (Feb 24 – Mar 7, 2026) · 7 participants · Duration: 80 minutes',
      },
      {
        type: 'h3',
        text: 'Start Doing (top voted)',
      },
      {
        type: 'table',
        headers: ['Card', 'Votes', 'Discussion Note'],
        rows: [
          ['Reviewing each other\'s PR before merging', '7', 'Currently no one reviews: leads to bugs in production'],
          ['Weekly 30-min design review before dev starts', '6', 'Designs are changing mid-sprint causing rework'],
          ['Sharing learnings from incidents in a post-mortem doc', '4', 'We keep solving the same problems'],
          ['Using feature flags for new checkout flows', '3', 'Enables safer releases without big-bang deploy'],
        ],
      },
      {
        type: 'h3',
        text: 'Stop Doing (top voted)',
      },
      {
        type: 'table',
        headers: ['Card', 'Votes', 'Discussion Note'],
        rows: [
          ['Moving tickets to Done without QA sign-off', '8', 'Three production bugs this sprint came from this'],
          ['Starting tickets without acceptance criteria', '6', 'Costs 2-3x more time fixing assumptions later'],
          ['Skipping the daily standup on Fridays', '4', 'Creates coordination gaps over the weekend'],
          ['Pinging individuals directly for review instead of using GitHub', '2', 'Creates invisible work and blocks others'],
        ],
      },
      {
        type: 'h3',
        text: 'Continue Doing (top voted)',
      },
      {
        type: 'table',
        headers: ['Card', 'Votes', 'Discussion Note'],
        rows: [
          ['Thursday pair programming sessions', '9', 'Best practice for spreading knowledge, unanimous support'],
          ['Using Slack for async decisions with 24h response expectation', '7', 'Reduces unnecessary sync meetings'],
          ['End-of-sprint mini-demos to stakeholders', '5', 'Keeps product aligned and builds confidence'],
          ['Sprint planning with explicit capacity buffer', '3', 'Reduced over-commitment compared to last quarter'],
        ],
      },
      {
        type: 'h3',
        text: 'Committed Action Items',
      },
      {
        type: 'table',
        headers: ['Action Item', 'Owner', 'Due', 'Linked to'],
        rows: [
          ['Add mandatory QA sign-off step to Jira ticket workflow', 'PM (Ana)', 'Mar 12', 'Top "Stop" item'],
          ['Add acceptance criteria template to ticket creation form', 'PM (Ana)', 'Mar 10', '#2 Stop item'],
          ['Schedule recurring design review: Mondays 10am', 'Designer (Lena)', 'Mar 9', '#2 Start item'],
          ['Create incident post-mortem doc template', 'Lead (Jin)', 'Mar 14', '#3 Start item'],
        ],
      },
      {
        type: 'h2',
        text: 'AI-Generated Summary',
      },
      {
        type: 'quote',
        text: 'AI Summary: Sprint 11, Checkout Squad (7 participants, Start/Stop/Continue):\n\nThe dominant theme is quality control friction. The top two "Stop" items (no QA sign-off and starting without acceptance criteria) both point to the same root cause: incomplete handoffs between product and engineering. Both items received the highest votes, suggesting this is a persistent and widely-felt pain.\n\nThe team\'s highest-voted "Continue" item, Thursday pair programming, was unanimous and should be explicitly protected in sprint planning to prevent it from getting cut under delivery pressure.\n\n4 action items committed, all with owners and due dates within the sprint. The design review schedule (Monday 10am) is a key dependency for reducing mid-sprint rework. Recommend reviewing all 4 at Sprint 12 kickoff before planning.\n\nSentiment: cautiously positive. The team is identifying clear systemic issues and showing willingness to commit to structural fixes.',
      },
      {
        type: 'h2',
        text: 'How to Run This Format',
      },
      {
        type: 'ol',
        items: [
          'Set a 10-minute timer: everyone writes cards independently (private mode)',
          'Reveal all cards at once, group similar ones together',
          'Dot vote: each person gets 5 votes to allocate across all cards',
          'Discuss the top 3 items per column (not every card)',
          'Commit to 3–4 action items with owners and due dates',
        ],
      },
      {
        type: 'cta',
        text: 'Run your Start Stop Continue retro and get an AI summary in minutes',
        href: '/ai-retrospective-summary',
        label: 'Try AI Summary →',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────
  // POST 9: 4Ls Retrospective Example
  // ─────────────────────────────────────────────────────────────────
  {
    slug: '4ls-retrospective-example',
    title: '4Ls Retrospective Example: Liked, Learned, Lacked, Longed For',
    description:
      'See a complete 4Ls retrospective example with real team cards, discussion notes, and an AI-generated summary report. Best used after major milestones or challenging sprints.',
    coverEmoji: '📚',
    category: 'Example',
    publishedAt: '2026-03-01',
    readingTime: 6,
    author: 'Retrosive Team',
    tags: [
      '4ls retrospective',
      '4ls example',
      'liked learned lacked longed for',
      'retrospective formats',
      'agile retrospective',
    ],
    content: [
      {
        type: 'p',
        text: 'The 4Ls retrospective format (Liked, Learned, Lacked, Longed For) goes deeper than Start/Stop/Continue. It separates positive experiences, knowledge gained, process gaps, and unmet desires into four distinct lenses. This makes it especially valuable after high-intensity sprints, major product launches, or project milestones.',
      },
      {
        type: 'h2',
        text: 'When to Use the 4Ls Format',
      },
      {
        type: 'ul',
        items: [
          'After a major product launch or milestone sprint',
          'When the team is processing a difficult period and needs to reflect deeply',
          'When previous retros have felt too surface-level or repetitive',
          'Quarterly retrospectives covering a longer period',
          'When you want to capture both team morale and process gaps in one session',
        ],
      },
      {
        type: 'h2',
        text: 'What Each Column Means',
      },
      {
        type: 'table',
        headers: ['Column', 'Question it answers', 'Typical cards'],
        rows: [
          ['Liked', 'What did we enjoy or appreciate?', 'Positive experiences, wins, team behaviors'],
          ['Learned', 'What new knowledge did we gain?', 'Technical insights, process discoveries, surprises'],
          ['Lacked', 'What was missing or insufficient?', 'Resources, clarity, skills, communication'],
          ['Longed For', 'What did we wish we had?', 'Tools, processes, support, practices not yet tried'],
        ],
      },
      {
        type: 'h2',
        text: 'Real Example: Data Team · Q1 Milestone Retro',
      },
      {
        type: 'p',
        text: 'Team: Data Platform (5 engineers, 1 analyst, 1 PM) · Post-Q1 milestone retrospective · 7 participants · Duration: 90 minutes',
      },
      {
        type: 'h3',
        text: 'Liked (top voted)',
      },
      {
        type: 'ul',
        items: [
          'The new data pipeline architecture: much more maintainable than the old one (8 votes)',
          'Cross-team collaboration with the ML team during the feature build (6 votes)',
          'Product manager availability during the sprint: quick decisions when blocked (5 votes)',
          'Team lunch the week before launch: boosted morale significantly (4 votes)',
          'The blameless incident post-mortem process we adopted (4 votes)',
        ],
      },
      {
        type: 'h3',
        text: 'Learned (top voted)',
      },
      {
        type: 'ul',
        items: [
          'Streaming architecture requirements need to be validated before sprint starts, not during (7 votes)',
          'Data schema changes require at least 2 weeks of lead time for downstream consumers (6 votes)',
          'Monitoring dashboards should be built before launch, not after the first incident (5 votes)',
          'Pair programming reduces review cycles more than solo work + async review (3 votes)',
        ],
      },
      {
        type: 'h3',
        text: 'Lacked (top voted)',
      },
      {
        type: 'ul',
        items: [
          'Clear data ownership documentation: unclear who owns what tables (7 votes)',
          'Testing environment that mirrors production data size (6 votes)',
          'Agreed-upon SLA definitions for data freshness (5 votes)',
          'Onboarding documentation for new data pipeline contributors (4 votes)',
          'Regular sync with the analytics team to catch downstream impact earlier (3 votes)',
        ],
      },
      {
        type: 'h3',
        text: 'Longed For (top voted)',
      },
      {
        type: 'ul',
        items: [
          'Automated data quality checks running on every pipeline job (8 votes)',
          'A data catalog tool to document schemas and ownership (7 votes)',
          'Dedicated 20% time for infrastructure improvements each sprint (5 votes)',
          'Better alerting: we found out about data lag from a stakeholder, not a monitor (5 votes)',
        ],
      },
      {
        type: 'h3',
        text: 'Committed Action Items',
      },
      {
        type: 'table',
        headers: ['Action Item', 'Owner', 'Due'],
        rows: [
          ['Create data ownership registry in Notion', 'Analyst (Rin)', 'Mar 20'],
          ['Set up basic data quality checks on top 3 pipelines', 'Dev team', 'Mar 24'],
          ['Add SLA definitions to data pipeline runbook', 'Lead (Paulo)', 'Mar 18'],
          ['Request staging environment with production-scale dataset from Infra', 'PM (Sarah)', 'Mar 12'],
        ],
      },
      {
        type: 'h2',
        text: 'AI-Generated Summary',
      },
      {
        type: 'quote',
        text: 'AI Summary: Q1 Milestone Retro, Data Platform (7 participants, 4Ls):\n\nThe team successfully delivered a major pipeline rewrite and values the new architecture. However, the retrospective surfaced a clear pattern: the team lacks the observability and documentation infrastructure to operate confidently at scale.\n\nThree of the four top "Lacked" items (ownership docs, testing environment, SLAs) and three of the four "Longed For" items (data quality checks, catalog, alerting) point to the same gap: the team is building products but not the supporting systems needed to operate them reliably.\n\nThis is a strategic investment conversation, not just a process fix. Recommend bringing the top "Longed For" items into the Q2 planning conversation with leadership, particularly the data catalog and dedicated infrastructure time.\n\nPositive signal: cross-team collaboration and PM availability scored highly, suggesting strong working relationships. Protect these conditions in Q2.',
      },
      {
        type: 'cta',
        text: 'Run your 4Ls retrospective and generate an AI summary automatically',
        href: '/ai-retrospective-summary',
        label: 'Generate AI Report →',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────
  // POST 10: Mad Sad Glad Retrospective Example
  // ─────────────────────────────────────────────────────────────────
  {
    slug: 'mad-sad-glad-retrospective-example',
    title: 'Mad Sad Glad Retrospective Example (Real Cards + AI Summary)',
    description:
      'See a real Mad Sad Glad retrospective with actual team cards, discussion highlights, and an AI-generated summary report you can share with stakeholders.',
    coverEmoji: '😊',
    category: 'Example',
    publishedAt: '2026-03-01',
    readingTime: 5,
    author: 'Retrosive Team',
    tags: [
      'mad sad glad retrospective',
      'mad sad glad example',
      'retrospective emotions',
      'team morale retrospective',
      'agile retrospective',
    ],
    content: [
      {
        type: 'p',
        text: 'Mad, Sad, Glad is a retrospective format built around emotional honesty. Instead of asking "what should we change?", it asks "how did this sprint make you feel?" This unlocks conversations that more structured formats often miss, especially around interpersonal friction, burnout risk, and team morale.',
      },
      {
        type: 'h2',
        text: 'When to Use Mad Sad Glad',
      },
      {
        type: 'ul',
        items: [
          'When team morale seems low or tense: this format lets people name it',
          'After a particularly difficult or stressful sprint',
          'When previous retros have been too process-focused and missed the human element',
          'For teams that have psychological safety and can discuss feelings openly',
          'As a complement to a more process-oriented format: rotate every 2–3 sprints',
        ],
      },
      {
        type: 'h2',
        text: 'Facilitation Tips Before You Start',
      },
      {
        type: 'ul',
        items: [
          'Remind the team: this is about work situations, not personal attacks on people',
          'Use anonymous mode in your retro tool: emotions are harder to share with your name attached',
          'Allow 12–15 minutes of private writing time before revealing cards',
          'As facilitator, share your own card first: it breaks the ice and signals safety',
        ],
      },
      {
        type: 'h2',
        text: 'Real Example: Growth Team · Sprint 22',
      },
      {
        type: 'p',
        text: 'Team: Growth Squad (3 engineers, 1 designer, 1 data analyst, 1 PM) · Sprint 22 (Feb 24 – Mar 7, 2026) · 6 participants · Duration: 75 minutes · Anonymous mode: enabled',
      },
      {
        type: 'h3',
        text: 'Mad (frustrations)',
      },
      {
        type: 'table',
        headers: ['Card', 'Votes', 'Discussion Note'],
        rows: [
          ['Requirements changing mid-sprint without impact assessment', '7', 'Third sprint in a row this happened: engineers losing trust in planning'],
          ['Being pulled into 3-4 unplanned meetings per week', '6', 'Destroys deep work blocks, especially for the engineers'],
          ['No feedback on the A/B test results we shipped 3 sprints ago', '5', 'We don\'t know if our work is having impact'],
          ['Design reviews happening after dev has already started', '4', 'Results in significant rework every time'],
        ],
      },
      {
        type: 'h3',
        text: 'Sad (disappointments)',
      },
      {
        type: 'table',
        headers: ['Card', 'Votes', 'Discussion Note'],
        rows: [
          ['The activation feature we worked hard on was deprioritized without explanation', '6', 'Team feels unheard in product decisions'],
          ['We haven\'t had a team social event since Q3 last year', '5', 'Remote team losing personal connection'],
          ['Two good engineers left the company this quarter', '4', 'Team morale impacted, not addressed directly'],
          ['Our velocity is down 20% from last quarter but nobody has acknowledged it', '3', 'Team wants the trend recognized, not hidden'],
        ],
      },
      {
        type: 'h3',
        text: 'Glad (positives)',
      },
      {
        type: 'table',
        headers: ['Card', 'Votes', 'Discussion Note'],
        rows: [
          ['The referral feature launched and early metrics look strong', '8', 'First time in months we could see direct impact'],
          ['New team member Kai onboarded super smoothly', '7', 'Pair programming during first two weeks made a difference'],
          ['PM responded to blockers within 1 hour this sprint', '5', 'Felt very supported compared to previous sprints'],
          ['We finally deprecated the legacy segment tracking code', '4', 'Long-overdue tech debt cleared, engineers relieved'],
        ],
      },
      {
        type: 'h3',
        text: 'Committed Action Items',
      },
      {
        type: 'table',
        headers: ['Action Item', 'Owner', 'Due'],
        rows: [
          ['PM to present A/B test results from last 3 sprints at next sprint review', 'PM (Carlos)', 'Mar 14'],
          ['Add "scope freeze date" to sprint planning: requirements locked after day 2', 'Scrum Master', 'Next sprint'],
          ['Team virtual coffee: schedule for last Friday of the month', 'Sarah (SM)', 'Mar 28'],
          ['Block 2h "deep work" windows on engineering calendars: no meetings', 'Each engineer', 'Mar 10'],
        ],
      },
      {
        type: 'h2',
        text: 'AI-Generated Summary',
      },
      {
        type: 'quote',
        text: 'AI Summary: Sprint 22, Growth Squad (6 participants, Mad/Sad/Glad, anonymous):\n\nThe emotional temperature of this team is strained. The "Mad" column is dominated by process failures that have repeated for 3+ sprints: mid-sprint requirement changes, meeting overload, and delayed design reviews. These are not new complaints, the fact that they recur suggests previous action items either weren\'t implemented or didn\'t hold.\n\nThe "Sad" column reveals a deeper concern: the team feels deprioritized and disconnected. The deprioritized activation feature, declining velocity going unacknowledged, and social isolation as a remote team are cumulative morale drains. Two engineer departures this quarter add context to the sentiment.\n\nBright spots are real: the referral launch, strong onboarding experience, and PM responsiveness this sprint show what the team can do when the conditions are right.\n\nRecommendation: the pattern of recurring "Mad" items suggests a structural conversation with leadership is overdue, particularly around meeting culture and mid-sprint scope changes. These require management commitment, not just team-level action items.',
      },
      {
        type: 'cta',
        text: 'Run your Mad Sad Glad retro and get an AI-generated summary',
        href: '/ai-retrospective-summary',
        label: 'Generate AI Summary →',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────
  // POST 11: Free Retrospective Report Template
  // ─────────────────────────────────────────────────────────────────
  {
    slug: 'retrospective-report-template-free',
    title: 'Free Retrospective Report Template (Copy & Use Today)',
    description:
      'Get free retrospective report templates you can copy and use immediately. Includes short-form, long-form, and action items formats, plus an AI-powered option to generate reports automatically.',
    coverEmoji: '🆓',
    category: 'Template',
    publishedAt: '2026-03-01',
    readingTime: 5,
    author: 'Retrosive Team',
    tags: [
      'retrospective report template',
      'free retro template',
      'sprint retrospective template',
      'retro report format',
      'agile retrospective',
    ],
    content: [
      {
        type: 'p',
        text: 'A good retrospective report template does two things: it makes the write-up fast for the facilitator, and it makes the output readable for anyone who wasn\'t in the room. Here are three free formats you can copy and adapt for your team today.',
      },
      {
        type: 'h2',
        text: 'Template 1: Short-Form (Best for Slack / Team Chat)',
      },
      {
        type: 'p',
        text: 'Use this when you want to share results quickly in a team channel. Aim for under 200 words.',
      },
      {
        type: 'quote',
        text: '📋 [Team Name] · Sprint [#] Retro Summary\n📅 [Date] · Format: [Template name] · [N] participants\n\n🔑 Key themes:\n• [Theme 1: 1 sentence]\n• [Theme 2: 1 sentence]\n• [Theme 3: 1 sentence]\n\n✅ Action items:\n1. [What] → [Owner] → [Due date]\n2. [What] → [Owner] → [Due date]\n3. [What] → [Owner] → [Due date]\n\n🔁 Previous sprint actions:\n• [Item]: ✅ Done / ⏳ In progress / ❌ Not done\n\nFull board: [link to retro board]',
      },
      {
        type: 'h2',
        text: 'Template 2: Long-Form (Best for Confluence / Notion)',
      },
      {
        type: 'p',
        text: 'Use this for stakeholder documentation, quarterly reviews, or when you want a detailed record.',
      },
      {
        type: 'table',
        headers: ['Section', 'Content'],
        rows: [
          ['Header', 'Team name · Sprint # · Date range · Facilitator · Participant count'],
          ['Format used', 'Template name + brief description (1 sentence)'],
          ['Top themes', '3–5 bullet points summarizing the most-voted topics and root causes'],
          ['Column highlights', 'Top 3–5 cards per column with vote counts'],
          ['Action items', 'Full table: what | owner | due date | status'],
          ['Previous sprint review', 'Each previous action item: done / in progress / dropped'],
          ['Team sentiment', 'Optional: 1–2 sentences on overall team mood and energy level'],
          ['Next retro date', 'Schedule the next session while it\'s top of mind'],
        ],
      },
      {
        type: 'h2',
        text: 'Template 3: Action Items Only (Best for Jira / Linear)',
      },
      {
        type: 'p',
        text: 'When your team tracks work in a project management tool, use this format to create tickets directly from your retro.',
      },
      {
        type: 'table',
        headers: ['Field', 'What to fill in'],
        rows: [
          ['Title', 'Retro Action: [brief description of the change]'],
          ['Description', '1-2 sentences on why this was raised + what "done" looks like'],
          ['Assignee', 'Single named owner (never "the team")'],
          ['Due date', 'Specific date: not "next sprint" or "soon"'],
          ['Label/Tag', 'retro-action (for easy filtering in future retro reviews)'],
          ['Linked to', 'The retrospective session or parent epic'],
        ],
      },
      {
        type: 'h2',
        text: 'How Teams Use These Templates',
      },
      {
        type: 'ul',
        items: [
          'Scrum Masters post the short-form to Slack within 30 minutes of the retro ending',
          'Long-form summaries go into Confluence for leadership quarterly reviews',
          'Action items are created as Jira tickets immediately, so they enter the next sprint',
          'The previous sprint review section keeps the team accountable across sessions',
        ],
      },
      {
        type: 'h2',
        text: 'Skip the Template Entirely: AI-Generated Reports',
      },
      {
        type: 'p',
        text: 'If your team runs retrospectives in Retrosive, you don\'t need to fill out any template manually. The AI report feature reads your board (all the cards, vote counts, and groupings) and generates a structured summary automatically. It takes about 10 seconds and produces a report in short-form, long-form, and action-items format simultaneously.',
      },
      {
        type: 'ul',
        items: [
          'No copy-pasting sticky notes into a doc',
          'Identifies themes and patterns across cards automatically',
          'Flags recurring issues from previous sessions',
          'Formats output for Slack, Confluence, or email sharing',
        ],
      },
      {
        type: 'tip',
        text: 'Run your retro in Retrosive, click "Generate AI Report" when the session ends, and paste the result into your team channel. The whole post-retro process takes under 2 minutes.',
      },
      {
        type: 'cta',
        text: 'Generate your retrospective report automatically: no template needed',
        href: '/ai-retrospective-summary',
        label: 'Try AI Report Generator →',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────
  // POST 12: Why Your Sprint Retrospective Needs AI in 2026
  // ─────────────────────────────────────────────────────────────────
  {
    slug: 'ai-sprint-retrospective',
    title: 'Why Your Sprint Retrospective Needs AI in 2026',
    description:
      'Manual retrospectives are slow, biased, and forget everything. Discover how AI transforms sprint retrospectives with automatic summaries, theme detection, and cross-sprint pattern analysis.',
    coverEmoji: '🤖',
    category: 'Guide',
    publishedAt: '2026-05-11',
    readingTime: 7,
    author: 'Retrosive Team',
    tags: [
      'AI sprint retrospective',
      'AI retrospective tool',
      'sprint retrospective automation',
      'agile AI',
      'retrospective summary AI',
    ],
    content: [
      {
        type: 'p',
        text: 'Sprint retrospectives are the most important recurring meeting in agile. They\'re where teams learn, adapt, and improve. Yet most teams spend 45 minutes in a meeting and walk away with a list of action items that nobody looks at until the next retro, if ever.',
      },
      {
        type: 'p',
        text: 'In 2026, AI changes this. Not with hype, but with practical automation that removes the most tedious parts of running a retrospective, and makes the insights actually stick.',
      },
      {
        type: 'h2',
        text: 'The Problem with Traditional Retrospectives',
      },
      {
        type: 'p',
        text: 'Before we talk about AI, let\'s be honest about what goes wrong with manual retros.',
      },
      {
        type: 'ul',
        items: [
          'The facilitator spends 10 minutes grouping sticky notes instead of facilitating discussion',
          'Action items get written in a doc that\'s never opened again',
          'The loudest voices shape the summary, not the most important feedback',
          'Nobody remembers what was discussed two sprints ago',
          'Writing up the retro report takes 20-30 minutes after the meeting',
        ],
      },
      {
        type: 'p',
        text: 'These aren\'t edge cases. They\'re the default experience for most agile teams. And the result is retrospectives that feel like a box-checking exercise rather than a genuine improvement mechanism.',
      },
      {
        type: 'h2',
        text: 'What AI Actually Does in a Sprint Retrospective',
      },
      {
        type: 'p',
        text: 'AI in retrospectives isn\'t about replacing the human conversation. It\'s about handling the parts that don\'t require human judgment, so your team can focus on the parts that do.',
      },
      {
        type: 'h3',
        text: '1. Automatic Summary Generation',
      },
      {
        type: 'p',
        text: 'When your retro session ends, AI reads every card, every vote, and every grouping, and produces a structured summary in seconds. Executive summary, key themes, decisions made, action items, and risks: all formatted and ready to share.',
      },
      {
        type: 'p',
        text: 'What used to take 20 minutes of post-meeting write-up now happens automatically. You can paste the result into Slack, Confluence, or email before people even leave the meeting room.',
      },
      {
        type: 'h3',
        text: '2. Theme Detection Without Bias',
      },
      {
        type: 'p',
        text: 'Humans group feedback based on what stands out to them. AI groups feedback based on semantic similarity: it reads all 40 sticky notes, not just the ones with the most votes.',
      },
      {
        type: 'p',
        text: 'This matters because low-vote feedback is often the most honest. Anonymous cards that didn\'t get voted up still represent real team sentiment. AI surfaces these patterns without the facilitator\'s unconscious bias.',
      },
      {
        type: 'h3',
        text: '3. Action Item Identification',
      },
      {
        type: 'p',
        text: 'AI reads discussion notes and identifies which items are genuine commitments versus general complaints. It suggests owners based on context and flags items that have appeared in previous sprints without resolution.',
      },
      {
        type: 'h3',
        text: '4. Cross-Sprint Pattern Analysis',
      },
      {
        type: 'p',
        text: 'This is where AI becomes truly powerful. A single retro gives you a snapshot. Multiple retros give you a trend. AI analyzes your last 6 sprints and tells you which problems are getting better, which are getting worse, and which ones your team keeps discussing without ever resolving.',
      },
      {
        type: 'quote',
        text: '"The same deployment issue has come up in 4 of your last 6 sprints. It was assigned as an action item twice but marked incomplete both times." (AI Insights Dashboard)',
      },
      {
        type: 'h2',
        text: 'Which Teams Benefit Most from AI Retrospectives?',
      },
      {
        type: 'h3',
        text: 'Scrum Masters and Agile Coaches',
      },
      {
        type: 'p',
        text: 'You\'re running retros for multiple teams. AI gives you consistent, structured output for every session without manual effort. You can focus on facilitation quality instead of note-taking.',
      },
      {
        type: 'h3',
        text: 'Engineering Managers',
      },
      {
        type: 'p',
        text: 'You need to report team health upward without sitting in every meeting. AI summaries give you a reliable, consistent view of what\'s happening at the team level, and what\'s being done about it.',
      },
      {
        type: 'h3',
        text: 'Remote and Distributed Teams',
      },
      {
        type: 'p',
        text: 'Remote retros lose context faster. People drop off. Notes are incomplete. AI ensures nothing gets lost: every card, every vote, every discussion thread gets captured and summarized.',
      },
      {
        type: 'h2',
        text: 'How to Start Using AI in Your Retrospectives',
      },
      {
        type: 'ol',
        items: [
          'Run your retrospective on Retrosive as usual: invite your team, add cards, vote, discuss',
          'When the session ends, click "Generate AI Report": it takes about 30 seconds',
          'Review the AI summary: it includes themes, action items, risks, and an executive summary',
          'Share to your team channel or documentation system',
          'After a few sprints, open the AI Insights Dashboard to see patterns across sessions',
        ],
      },
      {
        type: 'tip',
        text: 'You don\'t need to change how you run your retro. AI works on top of your existing process: it just removes the manual write-up step and adds cross-sprint intelligence.',
      },
      {
        type: 'h2',
        text: 'What AI Can\'t Replace',
      },
      {
        type: 'p',
        text: 'AI is not a facilitator. It doesn\'t create psychological safety. It doesn\'t ask follow-up questions. It doesn\'t read the room when someone looks uncomfortable. The human elements of a retrospective (trust, vulnerability, honest conversation) still require a skilled facilitator and a psychologically safe environment.',
      },
      {
        type: 'p',
        text: 'What AI does is remove the bureaucratic overhead so the human elements get more time and attention. Less time grouping sticky notes. More time having the conversation that actually matters.',
      },
      {
        type: 'cta',
        text: 'Try AI-powered retrospectives with Retrosive: free for your first session',
        href: '/ai-retrospective-summary',
        label: 'See AI Retrospective Summary →',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────
  // POST 13: AI Retrospective Summary Guide
  // ─────────────────────────────────────────────────────────────────
  {
    slug: 'ai-retrospective-summary-guide',
    title: 'AI Retrospective Summary: How to Get Instant Insights from Your Team\'s Feedback',
    description:
      'Learn how AI retrospective summaries work, what they include, how accurate they are, and how to use them to improve your sprint process in seconds instead of hours.',
    coverEmoji: '📊',
    category: 'Guide',
    publishedAt: '2026-05-11',
    readingTime: 8,
    author: 'Retrosive Team',
    tags: [
      'AI retrospective summary',
      'sprint retrospective report',
      'retrospective summary tool',
      'AI summary generator',
      'sprint insights',
    ],
    content: [
      {
        type: 'p',
        text: 'After every sprint retrospective, someone has to write up the summary. It means going back through notes, grouping themes, identifying action items, and formatting it into something shareable. That process takes 20-45 minutes. AI does it in 30 seconds.',
      },
      {
        type: 'p',
        text: 'This guide explains what an AI retrospective summary actually contains, how the AI generates it, and how to use it effectively in your team\'s workflow.',
      },
      {
        type: 'h2',
        text: 'What Is an AI Retrospective Summary?',
      },
      {
        type: 'p',
        text: 'An AI retrospective summary is a structured document automatically generated from your sprint retrospective data. Instead of reading each sticky note manually and writing a summary yourself, you run the AI analysis and get a formatted report in seconds.',
      },
      {
        type: 'p',
        text: 'A complete AI retrospective summary typically includes:',
      },
      {
        type: 'ul',
        items: [
          'Executive Summary: a 2-3 sentence overview of the session',
          'What Went Well: positive themes with supporting quotes from the board',
          'What Needs Improvement: problem areas ranked by frequency and vote count',
          'Key Decisions: anything the team agreed to change or try',
          'Action Items: specific tasks with suggested owners',
          'Risks and Blockers: issues flagged as urgent or systemic',
          'Team Sentiment: overall mood indicator based on card content and voting patterns',
        ],
      },
      {
        type: 'h2',
        text: 'How AI Generates the Summary',
      },
      {
        type: 'p',
        text: 'The AI reads your retrospective board data: all the cards, their column (What Went Well, What Didn\'t, Action Items, etc.), vote counts, and any groupings the facilitator created.',
      },
      {
        type: 'p',
        text: 'It then runs several analysis steps:',
      },
      {
        type: 'ol',
        items: [
          'Semantic grouping: cards with similar meaning are clustered together even if the words are different',
          'Importance scoring: combines vote count, card position, and discussion signals to rank topics',
          'Sentiment analysis: determines whether cards are positive, negative, or constructive',
          'Action item extraction: identifies commitments and suggestions from the card content',
          'Report generation: formats everything into a structured, readable document',
        ],
      },
      {
        type: 'h2',
        text: 'Example: Before and After AI Summary',
      },
      {
        type: 'h3',
        text: 'Before: Raw Retrospective Board',
      },
      {
        type: 'p',
        text: 'Imagine a team retro with 35 cards across What Went Well, What Didn\'t, and Action Items columns. Cards include things like "deploy pipeline was slow," "good communication in standup," "PR reviews taking too long," "new feature shipped on time," "too many meetings," and 30 more similar notes.',
      },
      {
        type: 'h3',
        text: 'After: AI Summary',
      },
      {
        type: 'quote',
        text: 'Executive Summary: The team had a productive sprint with a successful feature delivery, but continues to face friction from slow CI/CD pipelines and lengthy PR review cycles. Communication in standups was highlighted as a strength. Three action items were identified, with two assigned to specific owners.',
      },
      {
        type: 'p',
        text: 'The AI then lists the specific themes, the action items with suggested owners, and a sentiment score. What would have taken 30 minutes to write is ready before the meeting officially ends.',
      },
      {
        type: 'h2',
        text: 'How Accurate Are AI Retrospective Summaries?',
      },
      {
        type: 'p',
        text: 'For factual extraction (what cards were written, what was voted on, what action items were proposed), AI is essentially 100% accurate. It\'s reading structured data.',
      },
      {
        type: 'p',
        text: 'For interpretation (does "deploy pipeline" and "CI is slow" represent the same issue), modern AI models are very good but not perfect. You should review the summary before sharing it. In practice, most teams find the AI summary needs minor edits at most.',
      },
      {
        type: 'tip',
        text: 'Treat the AI summary as a first draft, not a final document. It typically gets you 90% of the way there in 30 seconds. A 2-minute human review completes the rest.',
      },
      {
        type: 'h2',
        text: 'Integrating AI Summaries into Your Workflow',
      },
      {
        type: 'h3',
        text: 'Share to Slack immediately',
      },
      {
        type: 'p',
        text: 'Generate the AI summary while the team is still in the meeting. Share it to your #team or #engineering channel before the call ends. This creates immediate visibility and accountability for action items.',
      },
      {
        type: 'h3',
        text: 'Link to your sprint documentation',
      },
      {
        type: 'p',
        text: 'Paste the summary into Confluence, Notion, or your wiki at the end of each sprint. After 3-4 sprints, you have a searchable history of what your team has been working through.',
      },
      {
        type: 'h3',
        text: 'Review action items at sprint planning',
      },
      {
        type: 'p',
        text: 'Use the AI summary as the first agenda item of your next sprint planning. What action items were committed to last sprint? Which ones are done? Which ones should roll over? AI-generated summaries make this review fast because everything is already structured.',
      },
      {
        type: 'h2',
        text: 'AI vs Manual Summary: Time Comparison',
      },
      {
        type: 'table',
        headers: ['Task', 'Manual', 'AI (Retrosive)'],
        rows: [
          ['Read all cards', '5-10 min', 'Instant'],
          ['Group by theme', '10-15 min', 'Instant'],
          ['Write executive summary', '5 min', 'Instant'],
          ['Extract action items', '5 min', 'Instant'],
          ['Format and share', '5 min', '1 min'],
          ['Total', '30-40 min', '1-2 min'],
        ],
      },
      {
        type: 'cta',
        text: 'Generate your AI retrospective summary in seconds: no sign-up required',
        href: '/ai-retrospective-summary',
        label: 'Try AI Summary Generator →',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────
  // POST 14: 5 Signs Retrospectives Are Broken
  // ─────────────────────────────────────────────────────────────────
  {
    slug: 'signs-retrospectives-broken-ai-fix',
    title: 'The 5 Signs Your Team\'s Retrospectives Are Broken (And How AI Fixes Them)',
    description:
      'Is your retrospective stuck in a rut? These 5 warning signs indicate your retro process has stopped delivering value, and here\'s how AI-powered tools fix each one.',
    coverEmoji: '🔧',
    category: 'Guide',
    publishedAt: '2026-05-11',
    readingTime: 6,
    author: 'Retrosive Team',
    tags: [
      'retrospective not working',
      'improve sprint retrospective',
      'fix retrospective',
      'retrospective problems',
      'agile improvement',
    ],
    content: [
      {
        type: 'p',
        text: 'Most teams know when their retrospectives have stopped working. The meeting feels routine. The same topics come up. Action items get forgotten. People stop engaging. But knowing the symptoms is different from knowing the fix.',
      },
      {
        type: 'p',
        text: 'Here are the five most common signs that your retrospectives are broken, and how AI-powered tools address each one.',
      },
      {
        type: 'h2',
        text: 'Sign #1: The Same Problems Come Up Every Sprint',
      },
      {
        type: 'p',
        text: 'You discuss "slow PR reviews" in sprint 4. You discuss it again in sprint 7. And sprint 11. The issue never gets resolved, but it keeps getting discussed.',
      },
      {
        type: 'p',
        text: 'This is the most common sign of a broken retrospective: recurring issues that get acknowledged but never acted on.',
      },
      {
        type: 'h3',
        text: 'How AI fixes it',
      },
      {
        type: 'p',
        text: 'AI tracks themes across all your sprints and flags recurring patterns. It alerts you when an issue has come up 3+ times without a resolved action item. Instead of discovering the pattern yourself after months, the AI surfaces it immediately: "This topic has appeared in 4 of your last 6 sprints."',
      },
      {
        type: 'h2',
        text: 'Sign #2: Action Items Are Never Done',
      },
      {
        type: 'p',
        text: 'Your team faithfully creates action items at the end of every retro. And faithfully ignores them until the next retro, when they appear again as new action items. The action item list is a graveyard.',
      },
      {
        type: 'h3',
        text: 'How AI fixes it',
      },
      {
        type: 'p',
        text: 'AI summarizes action items with clear ownership suggestions based on context ("this seems like an infrastructure issue: assign to DevOps?"). When you start the next sprint, the AI Insights Dashboard shows you which action items from last sprint are unresolved, making it impossible to ignore them.',
      },
      {
        type: 'h2',
        text: 'Sign #3: Half the Team Is Silent',
      },
      {
        type: 'p',
        text: 'Some team members write 10 cards. Others write one. Some never speak up in the group discussion. The retrospective is dominated by the loudest voices, and the quieter perspectives never surface.',
      },
      {
        type: 'h3',
        text: 'How AI fixes it',
      },
      {
        type: 'p',
        text: 'AI analysis is democratic. It reads every single card with equal weight, not just the ones that got discussed. Cards that were written but never voted on still get included in the AI summary, because someone wrote them for a reason. This gives quieter team members\'s feedback the same representation as the loudest voices.',
      },
      {
        type: 'tip',
        text: 'Combine AI summaries with anonymous mode in Retrosive to maximize psychological safety. Anonymous cards + AI analysis = the most honest picture of what your team actually thinks.',
      },
      {
        type: 'h2',
        text: 'Sign #4: Nobody Knows What Was Decided Last Sprint',
      },
      {
        type: 'p',
        text: 'You ask "did we decide to change the deploy process last sprint?" Nobody is sure. The notes from the retro are somewhere in a Google Doc that hasn\'t been opened since the meeting.',
      },
      {
        type: 'h3',
        text: 'How AI fixes it',
      },
      {
        type: 'p',
        text: 'AI summaries are generated automatically and stored in your retrospective history. Every decision, every action item, every theme: all structured, searchable, and timestamped. You can look up exactly what was discussed in sprint 8 in 10 seconds.',
      },
      {
        type: 'h2',
        text: 'Sign #5: The Facilitator Is Exhausted',
      },
      {
        type: 'p',
        text: 'Running a good retrospective is hard work. The facilitator has to group notes, manage time, keep the discussion on track, prevent rabbit holes, and then spend another 30 minutes writing up the summary after the meeting ends.',
      },
      {
        type: 'p',
        text: 'When the overhead becomes too high, facilitators start cutting corners: shorter sessions, less engagement, less thorough follow-up. The quality degrades.',
      },
      {
        type: 'h3',
        text: 'How AI fixes it',
      },
      {
        type: 'p',
        text: 'AI handles the administrative overhead so the facilitator can focus on facilitation. No manual grouping. No post-meeting write-up. The AI summary is ready before the call ends. The facilitator\'s energy goes entirely into creating a great conversation, which is the part that actually matters.',
      },
      {
        type: 'h2',
        text: 'Implementing the Fix',
      },
      {
        type: 'p',
        text: 'You don\'t need to overhaul your entire retrospective process to address these issues. The fix is straightforward:',
      },
      {
        type: 'ol',
        items: [
          'Switch to a tool with built-in AI (like Retrosive)',
          'Run your retro normally: the AI works in the background',
          'Generate the AI summary at the end of each session',
          'Review it as a team for 2 minutes before closing the meeting',
          'After 3-4 sprints, use the AI Insights Dashboard to spot patterns',
        ],
      },
      {
        type: 'cta',
        text: 'Fix your retrospectives with AI: start free today',
        href: '/ai-retrospective-summary',
        label: 'Try Retrosive AI →',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────
  // POST 15: Remote Sprint Retrospectives Guide
  // ─────────────────────────────────────────────────────────────────
  {
    slug: 'remote-sprint-retrospective-guide',
    title: 'Remote Sprint Retrospectives: The Complete Guide for Distributed Teams',
    description:
      'Running retrospectives with a remote team doesn\'t have to be painful. This guide covers tools, formats, facilitation techniques, and time zone strategies for distributed agile teams.',
    coverEmoji: '🌍',
    category: 'Guide',
    publishedAt: '2026-05-11',
    readingTime: 9,
    author: 'Retrosive Team',
    tags: [
      'remote sprint retrospective',
      'distributed team retrospective',
      'remote agile',
      'virtual retrospective',
      'remote scrum',
    ],
    content: [
      {
        type: 'p',
        text: 'Remote sprint retrospectives are harder than in-person ones. There\'s no whiteboard to gather around, no ability to read body language, and the energy of a physical room is replaced by a grid of faces on a video call, or worse, nobody has their camera on.',
      },
      {
        type: 'p',
        text: 'But remote retros don\'t have to be worse. With the right setup, they can actually be more inclusive and better documented than their in-person counterparts. Here\'s how.',
      },
      {
        type: 'h2',
        text: 'The Unique Challenges of Remote Retrospectives',
      },
      {
        type: 'ul',
        items: [
          'Engagement drops off: people multitask, check Slack, or zone out',
          'Silence is more awkward: nobody wants to be the first to speak into a void',
          'Technical issues eat into meeting time',
          'Time zones mean somebody is always in an inconvenient slot',
          'Notes and action items get lost without a shared physical space',
          'Psychological safety is harder to establish through a screen',
        ],
      },
      {
        type: 'h2',
        text: 'Choosing the Right Tool for Remote Retros',
      },
      {
        type: 'p',
        text: 'The tool you use for remote retrospectives matters more than it does in person. In a physical room, you can improvise with sticky notes on a whiteboard. Remote teams need a purpose-built platform.',
      },
      {
        type: 'p',
        text: 'Look for these features in a remote retrospective tool:',
      },
      {
        type: 'ul',
        items: [
          'Real-time collaboration: everyone sees changes instantly, no refresh required',
          'Anonymous mode: removes the discomfort of sharing negative feedback on camera',
          'Private writing phase: lets everyone add cards before anyone can see them, preventing groupthink',
          'Built-in timer: keeps sessions focused when there\'s no physical clock',
          'Export and summary features: captures decisions and action items automatically',
          'No installation required: participants can join with a link, no account needed',
        ],
      },
      {
        type: 'h2',
        text: 'Structuring Your Remote Retrospective',
      },
      {
        type: 'h3',
        text: 'Phase 1: Private Writing (10 minutes)',
      },
      {
        type: 'p',
        text: 'Start with anonymous card writing. Everyone adds their thoughts simultaneously without seeing each other\'s cards. This prevents the loudest voice from setting the agenda and gives quieter team members equal representation.',
      },
      {
        type: 'h3',
        text: 'Phase 2: Reveal and Group (5 minutes)',
      },
      {
        type: 'p',
        text: 'Reveal all cards at once. Group similar themes together as a team: this is quick in a good tool where everyone can drag cards simultaneously.',
      },
      {
        type: 'h3',
        text: 'Phase 3: Vote (5 minutes)',
      },
      {
        type: 'p',
        text: 'Give everyone a fixed number of votes. Dot voting tells you what matters most to the team, and removes the need for the facilitator to decide what gets discussed.',
      },
      {
        type: 'h3',
        text: 'Phase 4: Discussion (15-20 minutes)',
      },
      {
        type: 'p',
        text: 'Start with the highest-voted items. For remote teams, keep the camera rule: if you\'re speaking, camera on. It improves engagement dramatically.',
      },
      {
        type: 'h3',
        text: 'Phase 5: Action Items (5 minutes)',
      },
      {
        type: 'p',
        text: 'End with explicit action items. Each item needs an owner and a due date. If you\'re using Retrosive, generate the AI summary now: it structures everything that was discussed into a shareable report before the call ends.',
      },
      {
        type: 'h2',
        text: 'Facilitation Tips for Remote Teams',
      },
      {
        type: 'h3',
        text: 'Use anonymous mode by default',
      },
      {
        type: 'p',
        text: 'In person, teams develop trust over time and anonymous cards become less necessary. Remote teams take longer to build that psychological safety. Default to anonymous until the team explicitly asks to remove it.',
      },
      {
        type: 'h3',
        text: 'Keep sessions to 45 minutes',
      },
      {
        type: 'p',
        text: 'Remote fatigue is real. A 90-minute remote retro will lose everyone after 45 minutes. For a two-week sprint, 45 minutes is almost always enough. Use a timer and stick to it.',
      },
      {
        type: 'h3',
        text: 'Send a pre-retro prompt',
      },
      {
        type: 'p',
        text: 'Send a Slack message 30 minutes before the retro: "Think about one thing that slowed you down this sprint and one thing that worked well." This primes people to come with something to say and reduces the blank-stare silence at the start.',
      },
      {
        type: 'h2',
        text: 'Handling Multiple Time Zones',
      },
      {
        type: 'p',
        text: 'If your team spans 3+ time zones, you have two options: rotate the meeting time fairly so the inconvenience is shared, or switch to asynchronous retrospectives for some sprints.',
      },
      {
        type: 'p',
        text: 'For teams where live collaboration is important but time zones are a constraint, consider a hybrid approach: async card writing (24-48 hours) + a short synchronous discussion of the top themes.',
      },
      {
        type: 'tip',
        text: 'With Retrosive, participants can add cards before the meeting starts. Open the board 24 hours early and let everyone add their cards at their own time. Then use the live session for discussion only, cutting meeting time by half.',
      },
      {
        type: 'cta',
        text: 'Run your next remote retrospective with Retrosive: real-time, anonymous, AI-powered',
        href: '/ai-retrospective-summary',
        label: 'Start Free →',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────
  // POST 16: Async Retrospective Guide
  // ─────────────────────────────────────────────────────────────────
  {
    slug: 'async-retrospective-guide',
    title: 'Asynchronous Retrospectives: Run Retros Across Time Zones Without Scheduling Hell',
    description:
      'Async retrospectives let distributed teams skip the scheduling nightmare. Learn how to run effective asynchronous retros, what tools to use, and when async beats synchronous.',
    coverEmoji: '⏰',
    category: 'Guide',
    publishedAt: '2026-05-11',
    readingTime: 7,
    author: 'Retrosive Team',
    tags: [
      'async retrospective',
      'asynchronous retrospective',
      'distributed team retro',
      'remote retrospective tools',
      'async agile',
    ],
    content: [
      {
        type: 'p',
        text: 'Finding a 45-minute slot that works for a team spread across San Francisco, London, and Singapore is not a scheduling problem. It\'s an impossibility. Someone is always working at 6am or 11pm to make the meeting work.',
      },
      {
        type: 'p',
        text: 'Asynchronous retrospectives solve this. Instead of requiring everyone online at the same time, async retros let team members contribute on their own schedule, and still produce the same quality insights.',
      },
      {
        type: 'h2',
        text: 'What Is an Asynchronous Retrospective?',
      },
      {
        type: 'p',
        text: 'An asynchronous retrospective is a retro where participants contribute independently over a defined time window (usually 24-72 hours) rather than in a single live session.',
      },
      {
        type: 'p',
        text: 'The process looks like this:',
      },
      {
        type: 'ol',
        items: [
          'The facilitator opens a retrospective board and shares the link with the team',
          'Team members add cards and vote at their own time within the window (e.g., 48 hours)',
          'The facilitator reviews the results and generates an AI summary',
          'A short optional sync (15-20 min) discusses the top themes if needed',
          'Action items are assigned and shared with the whole team',
        ],
      },
      {
        type: 'h2',
        text: 'When Async Retrospectives Work Best',
      },
      {
        type: 'h3',
        text: 'Teams spread across 3+ time zones',
      },
      {
        type: 'p',
        text: 'When there\'s no overlap window that works for everyone, async removes the synchronization problem entirely. Everyone contributes when they\'re at their best, not at 6am when they\'re exhausted.',
      },
      {
        type: 'h3',
        text: 'High-meeting-load sprints',
      },
      {
        type: 'p',
        text: 'Some sprints are intense. Planning, daily standups, design reviews, stakeholder syncs: adding another 45-minute live session might push the team over the edge. An async retro takes 15 minutes of individual time and no coordination overhead.',
      },
      {
        type: 'h3',
        text: 'Teams where writing > talking',
      },
      {
        type: 'p',
        text: 'Some engineers are more articulate in writing than in verbal discussion. Async retros give these team members equal footing, and often produce more thoughtful, nuanced feedback than live brainstorming.',
      },
      {
        type: 'h2',
        text: 'When Sync Retrospectives Are Still Better',
      },
      {
        type: 'p',
        text: 'Async doesn\'t work for every situation. Avoid going async when:',
      },
      {
        type: 'ul',
        items: [
          'There\'s a significant team conflict that needs real-time resolution',
          'The sprint had a major incident that requires group processing',
          'Team trust is low and live discussion helps rebuild it',
          'You need live consensus on a major process change',
        ],
      },
      {
        type: 'h2',
        text: 'Running an Effective Async Retrospective',
      },
      {
        type: 'h3',
        text: 'Set a clear time window',
      },
      {
        type: 'p',
        text: 'Open the board on Monday. Close it Wednesday at 5pm UTC. Send a reminder on Tuesday afternoon. Make the window long enough (48h) that everyone in every time zone has at least one working day to contribute.',
      },
      {
        type: 'h3',
        text: 'Add a clear prompt',
      },
      {
        type: 'p',
        text: 'Don\'t just open the board and hope people know what to do. In your board description, add: "Add 2-3 cards to What Went Well, What Didn\'t, and at least 1 Action Item. Vote on other cards when you\'re done."',
      },
      {
        type: 'h3',
        text: 'Use anonymous mode',
      },
      {
        type: 'p',
        text: 'Async + anonymous is a powerful combination. Without the social pressure of a live meeting, combined with anonymity, team members write things they\'d never say out loud. This is where the most honest feedback comes from.',
      },
      {
        type: 'h3',
        text: 'Generate an AI summary before the optional sync',
      },
      {
        type: 'p',
        text: 'Before any optional sync meeting, generate the AI summary. Send it to the team in advance. The 15-minute discussion becomes focused on 2-3 themes rather than reading through all the cards from scratch.',
      },
      {
        type: 'tip',
        text: 'For fully distributed teams, make the sync optional. If the AI summary + action items are clear enough, many teams find they don\'t need the live discussion at all. Save the meeting for when it genuinely adds value.',
      },
      {
        type: 'h2',
        text: 'Async Retrospective Template',
      },
      {
        type: 'table',
        headers: ['Step', 'Who', 'When', 'How Long'],
        rows: [
          ['Open board + send prompt', 'Facilitator', 'Sprint end (Monday)', '5 min'],
          ['Add cards and vote', 'All team members', 'Mon-Wed', '10-15 min each'],
          ['Generate AI summary', 'Facilitator', 'Wednesday evening', '2 min'],
          ['Share summary to team', 'Facilitator', 'Wednesday evening', '2 min'],
          ['Optional sync discussion', 'Team', 'Thursday', '15-20 min'],
          ['Assign action items', 'Facilitator', 'Thursday', '5 min'],
        ],
      },
      {
        type: 'cta',
        text: 'Run your first async retrospective with Retrosive: AI summary included',
        href: '/ai-retrospective-summary',
        label: 'Start Free →',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────
  // POST 17: Retrosive vs EasyRetro
  // ─────────────────────────────────────────────────────────────────
  {
    slug: 'retrosive-vs-easyretro',
    title: 'Retrosive vs EasyRetro: Which Retrospective Tool Is Right for Your Team?',
    description:
      'A detailed comparison of Retrosive and EasyRetro: features, pricing, AI capabilities, ease of use, and which one is better for small teams vs enterprise agile teams.',
    coverEmoji: '⚔️',
    category: 'Comparison',
    publishedAt: '2026-05-11',
    readingTime: 7,
    author: 'Retrosive Team',
    tags: [
      'EasyRetro alternative',
      'EasyRetro vs Retrosive',
      'retrospective tool comparison',
      'best retro tool',
      'EasyRetro review',
    ],
    content: [
      {
        type: 'p',
        text: 'EasyRetro has been a popular choice for agile teams since 2018. It\'s simple, visually clean, and does the basics well. But in 2026, teams are asking for more, particularly AI features that save time and surface insights automatically.',
      },
      {
        type: 'p',
        text: 'This comparison covers both tools honestly, including their weaknesses, so you can make the right call for your team.',
      },
      {
        type: 'h2',
        text: 'Feature Comparison',
      },
      {
        type: 'table',
        headers: ['Feature', 'Retrosive', 'EasyRetro'],
        rows: [
          ['Real-time collaboration', '✅ Yes', '✅ Yes'],
          ['Anonymous mode', '✅ Yes', '✅ Yes'],
          ['Private writing phase', '✅ Yes', '✅ Yes'],
          ['AI summary generation', '✅ Yes', '❌ No'],
          ['Cross-sprint pattern analysis', '✅ Yes', '❌ No'],
          ['Action item assignment', '✅ Yes', '✅ Yes'],
          ['Built-in templates', '✅ 12+', '✅ 15+'],
          ['Export to PDF', '✅ Yes', '✅ Yes'],
          ['Vote system', '✅ Yes', '✅ Yes'],
          ['Timer', '✅ Yes', '✅ Yes'],
          ['No-account participation', '✅ Yes', '✅ Yes'],
        ],
      },
      {
        type: 'h2',
        text: 'The AI Difference',
      },
      {
        type: 'p',
        text: 'The biggest differentiator between Retrosive and EasyRetro in 2026 is AI. Retrosive generates an automated summary at the end of every session: themes, action items, team sentiment, and an executive summary. EasyRetro has no AI features.',
      },
      {
        type: 'p',
        text: 'For teams that spend 20-30 minutes after every retro writing up notes and creating summaries, this is a significant time savings, and a quality improvement, since AI-generated summaries are more consistent and less biased than manual write-ups.',
      },
      {
        type: 'h2',
        text: 'Ease of Use',
      },
      {
        type: 'p',
        text: 'Both tools are easy to use. EasyRetro has a slightly simpler interface: there\'s less to configure and the learning curve is minimal. Retrosive has more features, which means a slightly higher initial learning curve, but most teams are productive in their first session.',
      },
      {
        type: 'tip',
        text: 'If your team runs retros less than once a month and just needs something that works without setup, EasyRetro is fine. If you run regular sprints and want AI summaries and pattern tracking, Retrosive is the better long-term choice.',
      },
      {
        type: 'h2',
        text: 'Pricing Comparison',
      },
      {
        type: 'table',
        headers: ['Plan', 'Retrosive', 'EasyRetro'],
        rows: [
          ['Free tier', '2 AI reports/month, unlimited boards', 'Limited boards'],
          ['Team plan', '$X/month: unlimited boards + AI', '$Y/month: unlimited boards'],
          ['Business plan', '$Z/month: advanced AI + insights', 'Enterprise pricing'],
        ],
      },
      {
        type: 'h2',
        text: 'Who Should Choose EasyRetro?',
      },
      {
        type: 'ul',
        items: [
          'Teams that run occasional retros (not every sprint)',
          'Teams that don\'t need AI summaries or cross-sprint tracking',
          'Teams that prefer maximum simplicity over features',
          'Teams heavily invested in EasyRetro\'s specific template library',
        ],
      },
      {
        type: 'h2',
        text: 'Who Should Choose Retrosive?',
      },
      {
        type: 'ul',
        items: [
          'Teams running regular sprint retrospectives (every 1-2 weeks)',
          'Teams that want AI-generated summaries instead of manual write-ups',
          'Scrum Masters managing multiple teams who need consistent reporting',
          'Engineering managers who want cross-sprint pattern tracking',
          'Distributed teams that need strong async support',
        ],
      },
      {
        type: 'cta',
        text: 'Try Retrosive free: including AI retrospective summaries',
        href: '/ai-retrospective-summary',
        label: 'Start Free with Retrosive →',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────
  // POST 18: Retrosive vs Parabol
  // ─────────────────────────────────────────────────────────────────
  {
    slug: 'retrosive-vs-parabol',
    title: 'Retrosive vs Parabol: Comparing AI Features, Pricing, and Ease of Use',
    description:
      'Parabol is a powerful retrospective and meetings tool with Jira integration. Here\'s how it compares to Retrosive on AI capabilities, simplicity, pricing, and agile team fit.',
    coverEmoji: '🔬',
    category: 'Comparison',
    publishedAt: '2026-05-11',
    readingTime: 7,
    author: 'Retrosive Team',
    tags: [
      'Parabol alternative',
      'Parabol vs Retrosive',
      'retrospective tool comparison',
      'Parabol review',
      'agile retrospective tools',
    ],
    content: [
      {
        type: 'p',
        text: 'Parabol positions itself as a "better meetings" platform with strong retrospective features. It targets larger organizations, has deep Jira and GitHub integrations, and holds SOC 2 certification. Retrosive takes a different approach: simpler, faster, and AI-first.',
      },
      {
        type: 'p',
        text: 'Which one is right for your team depends a lot on your size, existing toolchain, and how much setup overhead you\'re willing to accept.',
      },
      {
        type: 'h2',
        text: 'Feature Comparison',
      },
      {
        type: 'table',
        headers: ['Feature', 'Retrosive', 'Parabol'],
        rows: [
          ['AI retrospective summary', '✅ Yes', '⚠️ Limited'],
          ['Cross-sprint pattern analysis', '✅ Yes', '❌ No'],
          ['Jira integration', '✅ Yes', '✅ Yes'],
          ['GitHub integration', 'Not needed', '✅ Yes'],
          ['Anonymous mode', '✅ Yes', '✅ Yes'],
          ['Private writing phase', '✅ Yes', '✅ Yes'],
          ['SOC 2 compliance', '❌ No', '✅ Yes'],
          ['Setup time (first retro)', '< 2 minutes', '5-10 minutes'],
          ['No-account participation', '✅ Yes', '❌ Requires account'],
          ['Pricing model', 'Per seat', 'Per seat'],
        ],
      },
      {
        type: 'h2',
        text: 'AI Features: The Key Differentiator',
      },
      {
        type: 'p',
        text: 'Parabol has meeting summaries but they\'re not the core product focus. Retrosive is built around AI as the primary value driver: every retrospective session ends with an AI-generated summary, action items, and sentiment analysis.',
      },
      {
        type: 'p',
        text: 'Retrosive also tracks patterns across sprints: identifying recurring issues, unresolved action items, and team sentiment trends over time. This cross-sprint intelligence is something Parabol doesn\'t offer.',
      },
      {
        type: 'h2',
        text: 'Ease of Use and Setup',
      },
      {
        type: 'p',
        text: 'Parabol is powerful but comes with setup overhead. Integrating with Jira and GitHub, configuring teams, and onboarding participants takes meaningful time. For enterprise teams with dedicated Scrum Masters who need that depth, this is fine.',
      },
      {
        type: 'p',
        text: 'Retrosive is designed for speed. Create a board, share a link, and participants join without creating an account. For teams that want to start their retro in under 2 minutes, this is a significant advantage.',
      },
      {
        type: 'h2',
        text: 'Jira Integration',
      },
      {
        type: 'p',
        text: 'Both tools connect to Jira. Parabol can push retrospective action items directly into Jira (and GitHub) tickets. Retrosive integrates Jira into the retro board itself: live sprint issues, velocity, and burndown charts alongside your session so facilitators have delivery context without switching tools.',
      },
      {
        type: 'p',
        text: 'If GitHub ticket sync from retro actions is essential, Parabol has that covered. If you want Jira sprint visibility plus AI summaries and cross-sprint retro intelligence, Retrosive is the stronger fit.',
      },
      {
        type: 'h2',
        text: 'Pricing',
      },
      {
        type: 'p',
        text: 'Both tools use per-seat pricing. Parabol\'s enterprise features and SOC 2 compliance come at a higher price point. Retrosive is more affordable for smaller teams and offers a more generous free tier.',
      },
      {
        type: 'h2',
        text: 'Who Should Choose Parabol?',
      },
      {
        type: 'ul',
        items: [
          'Enterprise teams that require SOC 2 compliance',
          'Organizations that need GitHub integration for engineering retrospectives',
          'Teams that want Parabol\'s structured enterprise meeting hierarchy',
          'Large teams where Parabol\'s organizational structure features add value',
        ],
      },
      {
        type: 'h2',
        text: 'Who Should Choose Retrosive?',
      },
      {
        type: 'ul',
        items: [
          'Teams that want AI-first retrospectives with automatic summaries',
          'Scrum Masters who need consistent, structured reporting without manual effort',
          'Startups and scale-ups that need simplicity and speed',
          'Teams that want Jira sprint context inside retros plus cross-sprint pattern tracking',
          'Anyone who wants to run a retro in under 2 minutes without account setup',
        ],
      },
      {
        type: 'cta',
        text: 'Try Retrosive free: AI summaries and cross-sprint insights included',
        href: '/ai-retrospective-summary',
        label: 'Start Free with Retrosive →',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────
  // POST 19: Best AI Retrospective Tools 2026
  // ─────────────────────────────────────────────────────────────────
  {
    slug: 'best-ai-retrospective-tools-2026',
    title: 'Best Retrospective Tools with AI Features in 2026',
    description:
      'The best retrospective tools in 2026 all have one thing in common: AI. Here\'s a ranked comparison of the top AI-powered sprint retrospective platforms by features, accuracy, and value.',
    coverEmoji: '🧠',
    category: 'Comparison',
    publishedAt: '2026-05-11',
    readingTime: 8,
    author: 'Retrosive Team',
    tags: [
      'best AI retrospective tool',
      'AI retrospective software',
      'retrospective tool with AI',
      'AI agile tools 2026',
      'sprint retrospective AI',
    ],
    content: [
      {
        type: 'p',
        text: 'In 2026, the best retrospective tools are the ones with meaningful AI features, not just a chatbot bolted on, but AI that genuinely reduces the overhead of running and documenting retrospectives.',
      },
      {
        type: 'p',
        text: 'We evaluated the leading retrospective platforms on four AI criteria: summary quality, action item detection, sentiment analysis, and cross-sprint pattern tracking. Here\'s what we found.',
      },
      {
        type: 'h2',
        text: 'What Makes a Good AI Retrospective Feature?',
      },
      {
        type: 'p',
        text: 'Not all AI features are equal. We looked for:',
      },
      {
        type: 'ul',
        items: [
          'Summary quality: does the AI actually understand the retro content, or does it just copy-paste?',
          'Action item extraction: does it identify specific, assignable tasks?',
          'Sentiment analysis: does it track team mood over time?',
          'Cross-sprint intelligence: does it connect insights from multiple sessions?',
          'Speed: how fast does the AI generate results?',
          'Accuracy: how often do teams need to correct AI output?',
        ],
      },
      {
        type: 'h2',
        text: '#1 Retrosive: Best AI Retrospective Tool Overall',
      },
      {
        type: 'p',
        text: 'Retrosive is built AI-first. Every retrospective session ends with a one-click AI report: executive summary, key themes, action items, risks, and team sentiment. The AI is trained specifically on retrospective content, which makes it significantly more accurate than general-purpose AI tools.',
      },
      {
        type: 'h3',
        text: 'AI features:',
      },
      {
        type: 'ul',
        items: [
          '✅ Automatic AI summary (themes, action items, sentiment, risks)',
          '✅ Cross-sprint pattern analysis (tracks recurring issues across 6+ sprints)',
          '✅ AI Insights Dashboard (team health score, sentiment timeline, unresolved action items)',
          '✅ Action item owner suggestions based on context',
          '✅ 30-second generation time',
        ],
      },
      {
        type: 'h3',
        text: 'Best for:',
      },
      {
        type: 'p',
        text: 'Scrum Masters, Engineering Managers, and agile teams running regular sprint retrospectives who want AI to handle post-retro documentation and cross-sprint tracking.',
      },
      {
        type: 'h2',
        text: '#2 Parabol: Best for Enterprise with AI',
      },
      {
        type: 'p',
        text: 'Parabol offers meeting summaries and basic AI assistance but focuses more on integration depth (Jira, GitHub, SOC 2) than on retrospective-specific AI. Its AI features are improving but not as comprehensive as Retrosive\'s.',
      },
      {
        type: 'h3',
        text: 'AI features:',
      },
      {
        type: 'ul',
        items: [
          '⚠️ Basic meeting summaries',
          '❌ No cross-sprint pattern analysis',
          '❌ No AI insights dashboard',
          '✅ SOC 2 compliant',
        ],
      },
      {
        type: 'h2',
        text: '#3 EasyRetro: Limited AI',
      },
      {
        type: 'p',
        text: 'EasyRetro is a clean, simple retrospective tool but has not yet built meaningful AI features. It\'s a good choice for basic retros but falls behind in 2026 for teams that want AI summaries.',
      },
      {
        type: 'h3',
        text: 'AI features:',
      },
      {
        type: 'ul',
        items: [
          '❌ No AI summary',
          '❌ No action item detection',
          '❌ No cross-sprint analysis',
        ],
      },
      {
        type: 'h2',
        text: '#4 Retrium: Focus on Facilitation, Not AI',
      },
      {
        type: 'p',
        text: 'Retrium is strong on guided facilitation and psychological safety features. It doesn\'t offer AI summaries or pattern analysis, but its facilitation workflows are mature and well-designed for enterprise teams.',
      },
      {
        type: 'h2',
        text: 'Comparison Table',
      },
      {
        type: 'table',
        headers: ['Tool', 'AI Summary', 'Pattern Analysis', 'Sentiment', 'Action Items AI', 'Overall AI Score'],
        rows: [
          ['Retrosive', '✅ Full', '✅ Yes', '✅ Yes', '✅ Yes', '⭐⭐⭐⭐⭐'],
          ['Parabol', '⚠️ Basic', '❌ No', '❌ No', '❌ No', '⭐⭐'],
          ['EasyRetro', '❌ No', '❌ No', '❌ No', '❌ No', '⭐'],
          ['Retrium', '❌ No', '❌ No', '❌ No', '❌ No', '⭐'],
        ],
      },
      {
        type: 'h2',
        text: 'The Verdict',
      },
      {
        type: 'p',
        text: 'If AI features are a priority (and they should be for any team running regular sprints in 2026), Retrosive is the clear leader. It\'s the only tool that offers a complete AI retrospective workflow: automatic summaries, action item detection, sentiment analysis, and cross-sprint pattern tracking.',
      },
      {
        type: 'p',
        text: 'Other tools are either playing catch-up on AI or have deliberately chosen not to invest in it. For teams that want to spend less time on retrospective documentation and more time actually improving, Retrosive is the right choice.',
      },
      {
        type: 'cta',
        text: 'Try the best AI retrospective tool: free for your first sessions',
        href: '/ai-retrospective-summary',
        label: 'Start Free with Retrosive →',
      },
    ],
  },
];

export function getBlogPost(slug: string): BlogPost | undefined {
  return blogPosts.find((p) => p.slug === slug);
}
